import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronDown, ChevronRight, RefreshCw, Wallet, ArrowDownLeft,
  Receipt, Gift, Circle, ExternalLink, Search, ArrowLeft, Star, StickyNote, Scale, TrendingUp, Layers, Landmark, Link2, Sparkles, KeyRound,
  Pencil, Copy, Check as CheckIcon, Banknote, X, Bot, Target, Trash2, CalendarDays,
} from 'lucide-react';
import { ACTIVE_BOTS, PENDING_BOT_KEY } from '../activeBots';
import DateRangePicker, { rangeToMs } from '../components/DateRangePicker';
import BluefinAuthorize from '../components/BluefinAuthorize';
import SuiSwapAddModal from '../components/SuiSwapAddModal';
import ManualSwapModal from '../components/ManualSwapModal';
import ArcExitModal from '../components/ArcExitModal';
import { v4TokenId } from '../lib/arcExit';
import { useSuiClient } from '@mysten/dapp-kit';
import { useWS } from '../WSContext';
import { groupLabel, groupTitle, groupNameSuggestion } from '../lib/groupLabel';

// ── Token logoları ──────────────────────────────────────────────────────────────
// Öncelik: (1) override — hep quote olan ve DexScreener'da çözülemeyen majörler,
// (2) DexScreener API (base-token eşleştirmeli), (3) on-chain CoinMetadata.iconUrl,
// (4) harf-daire fallback. Sonuç coinType başına cache'lenir (in-flight dedup).
const LOGO_OVERRIDE: Record<string, string> = {
  '0x2::sui::SUI': 'https://s2.coinmarketcap.com/static/img/coins/64x64/20947.png',
  '0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI': 'https://s2.coinmarketcap.com/static/img/coins/64x64/20947.png',
  '0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7::usdc::USDC': 'https://s2.coinmarketcap.com/static/img/coins/64x64/3408.png',
  '0x5d4b302506645c37ff133b98c4b50a744f84776c9c7e38b33f6d6c6c64de3cb::coin::COIN': 'https://s2.coinmarketcap.com/static/img/coins/64x64/3408.png',
  '0xc060006111016b8a020ad5b33834984a437aaa7d3c74c18e09a95d48aceab08c::coin::COIN': 'https://s2.coinmarketcap.com/static/img/coins/64x64/825.png',
};
const _logoCache = new Map<string, Promise<string | null>>();

async function _dexImage(coinType: string): Promise<string | null> {
  try {
    const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${coinType}`);
    const d = await r.json();
    const pairs: any[] = d?.pairs || [];
    for (const p of pairs) {
      if ((p?.baseToken?.address || '').toLowerCase() === coinType.toLowerCase()) {
        const u = p?.info?.imageUrl;
        if (u) return u;
      }
    }
  } catch { /* yut */ }
  return null;
}

function resolveLogo(coinType: string, client: any): Promise<string | null> {
  if (LOGO_OVERRIDE[coinType]) return Promise.resolve(LOGO_OVERRIDE[coinType]);
  const cached = _logoCache.get(coinType);
  if (cached) return cached;
  const pr = (async () => {
    const dex = await _dexImage(coinType);
    if (dex) return dex;
    try { const m: any = await client.getCoinMetadata({ coinType }); if (m?.iconUrl) return m.iconUrl; } catch { /* yut */ }
    return null;
  })();
  _logoCache.set(coinType, pr);
  return pr;
}

function TokenIcon({ coinType, symbol, size = 36 }: { coinType: string; symbol: string; size?: number }) {
  const client = useSuiClient();
  const [url, setUrl] = useState<string | null>(() => LOGO_OVERRIDE[coinType] ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!coinType) { setUrl(null); return; }
    let alive = true;
    resolveLogo(coinType, client).then(u => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [coinType, client]);

  const px = `${size}px`;
  if (url && !failed) {
    return <img src={url} alt={symbol} onError={() => setFailed(true)}
      style={{ width: px, height: px }}
      className="rounded-full object-cover bg-[#03112b] border border-[#0a2560]" />;
  }
  return (
    <div style={{ width: px, height: px }}
      className="rounded-full bg-sky-500/15 text-sky-300 flex items-center justify-center text-[11px] font-semibold">
      {symbol?.slice(0, 3)}
    </div>
  );
}

// İki token'ın üst üste binen logoları (LP çifti)
function PairIcons({ a, b, sa, sb }: { a: string; b: string; sa: string; sb: string }) {
  return (
    <div className="flex items-center shrink-0" style={{ width: '52px' }}>
      <div className="relative z-10"><TokenIcon coinType={a} symbol={sa} size={34} /></div>
      <div className="relative -ml-3"><TokenIcon coinType={b} symbol={sb} size={34} /></div>
    </div>
  );
}

// ── Tipler (backend get_lp_positions / get_lp_position_events ile eşleşir) ──────
interface Reward { symbol: string; amount: number; usd: number; }
interface LpPosition {
  positionId: string; wallet: string; platform: string;
  tokenA: string; tokenB: string; symbolA: string; symbolB: string;
  tickLower: number | null; tickUpper: number | null;
  status: string; openedAt: number; closedAt: number;
  depositA: number; depositB: number; withdrawA: number; withdrawB: number;
  costA: number; costB: number;   // compound hariç gerçek maliyet (deposit gösterimi)
  feeA: number; feeB: number; rewards: Reward[];
  favorite: boolean; note: string; zap: boolean; zapOut: boolean;
  feeUsd: number; rewardUsd: number; costUsd: number;
  curA: number; curB: number; valueUsd: number | null;
  uncFeeA: number; uncFeeB: number;                        // toplanmamış fee (claim edilmemiş)
  uncRewards: { symbol: string; amount: number; usd: number }[];
  uncUsd: number | null;                                   // null = hiç okunmadı
  inRange: number | null;   // 1=aralıkta, 0=aralık dışı, null=bilinmiyor (kapalı/okunamadı)
  curTick: number | null;   // açık pozisyonun anlık on-chain token miktarı + USD değeri
  chain: string;   // 'sui' | 'solana' | 'arc' — explorer linki ve rozet için
  // Ödenen masraflar (Arc: gaz + launch ücreti) — fee'den ayrı, net'e dahil
  paid?: { kind: string; symbol: string; amount: number; usd: number }[];
  paidUsd?: number;
  decA?: number; decB?: number;          // tick → fiyat çevirisi için
  tickSpacing?: number | null;           // v4: tek aralık genişliği = aralık emri
  incomplete: boolean;   // withdraw var, deposit yok → net kâr/fiyat iddia edilemez
  seq: number;   // kronolojik pozisyon numarası (#N, cüzdan başına; #1 = en eski)
  openedTs: number;   // efektif açılış zamanı (opened_at, yoksa ilk event) — tarih filtresi
  groupNo: number; groupRank: number; role: string;             // manuel delta-grubu (0 = gruplanmamış; role sadece etiket)
  groupStatus: string;                       // grubun durumu: 'living' | 'closed' (manuel etiket)
  priceA: number | null; priceB: number | null;   // canlı token fiyatları (delta USD hesabı için)
}
// Funding arbitrage perp bacağı — işaretli trade'lerden türetilir (backend: get_lp_group_perp)
interface PerpLeg {
  groupNo: number; botId: string; symbol: string;
  netQty: number; avgEntry: number; realizedPnl: number; fee: number;
  sides?: Record<string, { qty: number; avgPrice: number; fee: number; count: number; realizedPnl: number }>;
  fundingUsd: number; startTs: number; tradeCount: number;
  live?: { side: string; size: number; entryPrice: number; markPrice: number;
           unrealizedPnl: number; leverage: number; notional: number };
}
// Cüzdan swap'ı — LP olmayan bacak (grubu swap'la kapatınca PnL eksik kalmasın)
interface Swap {
  chain: string; signature: string; ts: number;
  inSymbol: string; inAmount: number; outSymbol: string; outAmount: number;
  price: number; isLpTx: boolean; groupNo: number; role: string;
  gas?: number;    // Sui: gazdan arındırılmış fiyat + ayrı maliyet
  note?: string;   // elle eklenen (zincir dışı) bacakta kaynak notu
}
interface LpEvent {
  txHash: string; eventSeq: number; kind: string; platform: string;
  amountA: number; amountB: number; rewardSymbol: string; rewardAmount: number; timestamp: number; isZap: boolean; bundled?: boolean;
}

// ── Yardımcılar ────────────────────────────────────────────────────────────────
// Tur süresi: 2 günden kısa turlar saat olarak daha okunur.
const fmtSure = (ms: number) => {
  const h = (ms || 0) / 3_600_000;
  return h >= 48 ? `${(h / 24).toFixed(1)} gün` : `${h.toFixed(1)} sa`;
};
// 1'in altındaki miktarlarda 8 ondalığa çıkılır (kullanıcı, 01.11.2026): ETH/BTC
// gibi pahalı tokenlarda 4 ondalık gerçek para saklıyor — 0,000012 ETH "0,0000"
// görünüyordu. Sembol listesi yerine BÜYÜKLÜK ölçütü: kitaptaki tüm ETH/BTC
// miktarları 1'in altında (en büyüğü 0,5589 ETH) → hepsi kapsanıyor ve listeye
// eklenmeyen yeni pahalı token sessizce dışarıda kalmıyor. Yalnız RAKAM EKLER:
// minimumFractionDigits 0 olduğu için 0,0295 hâlâ "0,0295" basılıyor.
const fmt = (n: number, d = 4) =>
  (n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(n) < 1 && d < 8 ? 8 : d,
  });
const shortId = (s: string) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '');
const fmtDate = (ms: number) =>
  ms ? new Date(ms).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtPrice = (n: number) =>
  n >= 1 ? n.toLocaleString('en-US', { maximumFractionDigits: 4 }) : parseFloat(n.toPrecision(4)).toString();
const fmtSigned = (n: number) => (n > 0 ? '+' : '') + fmt(n);   // +/- işaretli (balance change gösterimi)
const fmtUsd = (n: number) => '$' + (n || 0).toLocaleString('en-US', { maximumFractionDigits: n >= 100 ? 0 : 2 });

// USD değerinin üzerine gelince token-bazında kırılım gösteren sarmalayıcı (CSS-only, group-hover)
// placement: yukarı/aşağı aç (sayfa üstündeki tile'lar 'bottom' kullanmalı → taşma olmaz)
// align: yatay hizalama ('left' sağ kenar taşmasını önler). Uzun listeler kaydırılabilir (max-h + scroll).
function UsdHover({ children, tokens, pop, className = '', placement = 'top', align = 'center' }:
  { children: React.ReactNode; tokens: { sym: string; amount: number }[]; pop: string; className?: string;
    placement?: 'top' | 'bottom'; align?: 'center' | 'left' | 'right' }) {
  if (!tokens || tokens.length === 0) return <span className={`inline-block ${className}`}>{children}</span>;
  const vert = placement === 'bottom' ? 'top-full' : 'bottom-full';   // boşluksuz → uzun listede scroll'a ulaşılabilir
  const horiz = align === 'left' ? 'left-0' : align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2';
  return (
    <span className={`relative group cursor-help inline-block ${className}`}>
      {children}
      <span className={`absolute ${horiz} ${vert} z-30 hidden group-hover:block
        max-h-[55vh] overflow-y-auto overscroll-contain whitespace-nowrap rounded-md border px-2.5 py-1.5
        text-[11px] leading-relaxed shadow-xl ${pop}`}>
        {tokens.map((t, i) => (
          <span key={i} className="block font-mono tabular-nums">{fmt(t.amount)} <span className="opacity-70">{t.sym}</span></span>
        ))}
      </span>
    </span>
  );
}

// Token bazında net = withdraw + fee + reward − deposit (fee+reward DAHİL).
//  - pair'in her iki token'ında da net + → NET KÂR (kind:'profit'); pair dışı reward
//    token'ları (3./4. token) da listelenir.
//  - değilse ve deposit/withdraw temiz takas ise → ORTALAMA FİYAT (kind:'price')
//    (fiyat swap fiyatı olduğundan yalnızca deposit/withdraw'dan hesaplanır).
// Fiyatın gösterileceği quote token önceliği: önce stable (USDC/USDT), sonra SUI.
// Pair'de bunlardan biri varsa fiyat o token cinsinden ("1 base = X quote").
// Sıra ÖNCELİKTİR: önce dolar stabilleri, sonra diğer stabiller, en son majörler.
// RWA/hisse tokenları (NVDA, TSLAx, SPYx…) burada YOK — olmamaları gerekiyor:
// fiyat "1 NVDA = 225 USDG" diye okunmalı, tersi ("1 USDG = 0,00444 NVDA") hiçbir
// şey ifade etmiyor. Yeni bir stablecoin çıktığında listeye eklemek yeterli.
// Fiyatın HANGİ tokenle kote edileceği. Sıra önemli: eşleşen ilk token quote olur.
// SUI en SONDA olmalı — çünkü bir dolar token'ıyla eşleşen SUI'de quote dolar
// tarafı olmalı ("1 SUI = 1,0099 USDSUI"), tersi okunamaz ve TERS görünür.
//
// 19.10.2026'da bildirilen hata: USDSUI/SUI paritesinde fiyat "SUI/USDSUI" diye
// kote ediliyordu. Sebep listede USDSUI'nin olmaması, SUI'nin olmasıydı — tek
// eşleşen SUI olunca quote o seçiliyordu. Sui'deki stabil token'lar tarandı,
// AYNI tuzaktaki beş token bulundu: AUSD (575 işlem!), BUCK, USDSUI, USDB,
// suiUSDe. Yenisi çıkarsa bulmanın yolu: lp_transactions'taki sembolleri çekip
// adında USD geçip de bu listede olmayanları listelemek.
// Stabil token'lar ayrı listede: hem quote önceliğini besliyor hem de
// "stablecoinler ARASI fiyat hesaplanmasın" kuralının dayanağı (bkz. choosePair).
const STABLES = [
  'USDC', 'USDT', 'USDG', 'USDS', 'DAI', 'PYUSD', 'FDUSD', 'RLUSD', 'USDe', 'USD1',
  'TUSD', 'USDY', 'EURC',
  // Sui ekosistemindeki stabil token'lar — SUI'den ÖNCE gelmeli
  'AUSD', 'BUCK', 'USDSUI', 'USDB', 'suiUSDe',
];
const QUOTE_PRIORITY = [...STABLES, 'SUI'];
const isStable = (s: string) =>
  STABLES.some(q => q.toUpperCase() === (s || '').toUpperCase());
// SOL'lu pariteler: SOL TABAN olsun → "1 SOL = 33.967 PUMP" (tersi 0,0000294 gibi okunamaz çıkar)
const BASE_PREFERRED = ['SOL'];
const pickQuote = (a: string, b: string): string | null => {
  const up = (x: string) => (x || '').toUpperCase();
  for (const q of QUOTE_PRIORITY) { if (up(a) === up(q)) return a; if (up(b) === up(q)) return b; }
  for (const s of BASE_PREFERRED) {   // taban tercihli token varsa quote DİĞER token olur
    if (up(a) === up(s)) return b;
    if (up(b) === up(s)) return a;
  }
  return null;
};
type PosMetric =
  | { kind: 'profit'; items: { sym: string; amt: number }[]; approx: boolean }
  | { kind: 'price'; price: number; base: string; quote: string; approx: boolean; dA: number; dB: number };
function posMetric(p: LpPosition): PosMetric | null {
  if (p.incomplete) return null;   // deposit kaydı yok → kâr/fiyat yanıltıcı olurdu
  if (p.withdrawA <= 0 && p.withdrawB <= 0) return null;   // withdraw yok
  const approx = p.status !== 'closed';
  // reward'ları sembol bazında topla
  const rw: Record<string, number> = {};
  for (const r of p.rewards) rw[r.symbol] = (rw[r.symbol] || 0) + r.amount;
  const rA = rw[p.symbolA] || 0, rB = rw[p.symbolB] || 0;
  // Ödenen masraf (gaz/launch) pair token'ındansa net'ten düşülür
  // Kira hariç: kilitlenip kapanışta geri dönüyor, masraf değil. Dahil etmek açık
  // Solana pozisyonunu ~0,0067 SOL zararlı gösterirdi (grup kartındaki ayrımın aynısı).
  const paidOf = (sym: string) => (p.paid || [])
    .filter(x => x.symbol === sym && x.kind !== 'rent' && x.kind !== 'rent_back')
    .reduce((a, x) => a + x.amount, 0);
  const netA = p.withdrawA + p.feeA + rA - p.costA - paidOf(p.symbolA);   // fee+reward dahil net (maliyet = compound hariç)
  const netB = p.withdrawB + p.feeB + rB - p.costB - paidOf(p.symbolB);
  const extras = Object.entries(rw)
    .filter(([s]) => s !== p.symbolA && s !== p.symbolB)
    .map(([sym, amt]) => ({ sym, amt }))
    .filter(e => e.amt > 0);   // pair dışı reward token'ları
  if (netA > 0 && netB > 0)
    return { kind: 'profit', approx, items: [{ sym: p.symbolA, amt: netA }, { sym: p.symbolB, amt: netB }, ...extras] };
  // temiz takas → ortalama fiyat (yalnızca deposit/withdraw)
  const dA = p.withdrawA - p.costA, dB = p.withdrawB - p.costB;
  const cleanSwap = (dA < 0 && dB > 0) || (dB < 0 && dA > 0);
  if (!cleanSwap) return null;
  const absA = Math.abs(dA), absB = Math.abs(dB);
  const quote = pickQuote(p.symbolA, p.symbolB);
  // Fiyat = quote miktarı / base miktarı → "1 base = price quote". Quote önce USDC/USDT, sonra SUI;
  // hiçbiri yoksa fallback: net verilen token quote sayılır.
  let baseSym: string, quoteSym: string, price: number;
  if (quote === p.symbolA)      { quoteSym = p.symbolA; baseSym = p.symbolB; price = absA / absB; }
  else if (quote === p.symbolB) { quoteSym = p.symbolB; baseSym = p.symbolA; price = absB / absA; }
  else {
    // Bilinmeyen çift: iki yönden 1'e daha yakın/büyük olanı seç. "1 X = 225 Y"
    // okunur, "1 Y = 0,00444 X" okunmaz. (SOL kuralı zaten büyük sayı üretiyor.)
    const pAB = absA / absB;                    // 1 B kaç A eder (ters yön 1/pAB'dir)
    if (pAB >= 1) { quoteSym = p.symbolA; baseSym = p.symbolB; price = pAB; }
    else          { quoteSym = p.symbolB; baseSym = p.symbolA; price = 1 / pAB; }
  }
  return { kind: 'price', price, base: baseSym, quote: quoteSym, approx, dA, dB };
}

// ── Delta grupları ────────────────────────────────────────────────────────────
// Grup = aynı group_no'yu paylaşan pozisyonlar (bir yönlü trade'in parçaları).
// Delta (fee/reward HARİÇ) = Σ(withdraw − deposit) sembol bazında:
//   • A ve B aynı yönlü  → net kâr (ikisi de +) / net zarar (ikisi de −)
//   • ters yönlü         → ortalama fiyat (takas): |Δquote| / |Δbase|
// Fee+reward ayrı satırda; toplam = delta + fee + reward.
type TokAmt = { sym: string; amount: number; usd: number };
interface GroupCalc {
  no: number; rank: number; members: LpPosition[];
  delta: TokAmt[]; feeReward: TokAmt[]; total: TokAmt[];
  // Açık üyelerin HAVUZDA duran anlık bakiyesi. Delta yalnız gerçekleşen akışı ölçer;
  // açık pozisyonun yatırılmış sermayesi orada eksi görünür ama kaybedilmiş değildir.
  // Ayrı satır: "net delta" akış anlamını korusun, Toplam ise gerçek sonucu versin.
  pool: TokAmt[]; poolUsd: number;
  // Maruziyet = delta + havuz → hedge açığı bundan hesaplanmalı (akıştan değil).
  exposure: TokAmt[];
  fee: TokAmt[]; reward: TokAmt[];   // özet için ayrı (kartta birleşik gösterilir)
  cost: TokAmt[]; costUsd: number;   // ödenen masraf (gaz + launch ücreti) — Toplam'dan düşülür
  // Solana kirası AYRI: pozisyon hesaplarında KİLİTLENİR ve kapanışta tamamı geri
  // döner → masraf değil, geçici olarak bağlanmış sermaye. Toplam'dan DÜŞÜLMEZ;
  // düşmek açık pozisyonu ~0,0067 SOL zararlı gösterirdi. Kapalı pozisyonda
  // rent ile rent_back sıfırlanır ve satır kendiliğinden kaybolur.
  rent: TokAmt[]; rentUsd: number;
  deltaUsd: number; feeRewardUsd: number; totalUsd: number;
  feeUsd: number; rewardUsd: number;
  kind: 'profit' | 'loss' | 'price' | 'mixed';
  price?: number; base?: string; quote?: string;
  priceFrom?: number;   // ort. fiyatı kaç üye besledi (üye sayısından azsa ipucu gösterilir)
  // Fee+reward DAHİL ortalama fiyat — yalnız KAPALI üyelerden (+ swap bacakları).
  // Üstteki `price` net delta'dan, yani fee/reward hariç kuruluyor; bu ikisi aynı
  // taban/quote ile gösterilince "fee ortalamayı nereye çekti" doğrudan okunur.
  totalPrice?: number; totalBase?: string; totalQuote?: string;
  closedCount: number;   // fiyatı besleyen kapalı üye sayısı (üye sayısından azsa ipucu)
  hasOpen: boolean;   // üyelerden biri hâlâ açık → rakamlar geçici
  swapCount: number;  // gruba bağlı swap bacağı sayısı
  swaps: Swap[];      // satır olarak da çizilir (LP üyeleriyle aynı biçimde)
  // Delta / hedef ayrımı — yalnız satırlar elle etiketlendiyse dolu (bkz. computeSplit).
  // Kart bu alan null'ken birebir eski görünümünü koruyor.
  split?: GroupSplit | null;
  status: string;     // 'living' | 'closed' (manuel etiket; pozisyon durumundan türetilmez)
}
// Round-trip eşiği: kalan delta, brüt akışın bu oranından küçükse o token "kapanmış"
// sayılır (yönlü pozisyon kalmamış) → ort. fiyat yerine kâr/zarar gösterilir.
// (Tanım buraya, yorumunun yanına alındı: `pairAvgPrice` de bunu kullanıyor.)
const RESIDUAL_EPS = 0.15;

// Tek pozisyonun kendi takas fiyatı (gruptan bağımsız) — hem grup ortalamasında
// hem satır gösteriminde kullanılır.
function rawMemberPrice(m: LpPosition): { price: number; base: string; quote: string; baseAmt: number } | null {
  const dA = m.withdrawA - m.costA, dB = m.withdrawB - m.costB;
  if (!dA || !dB) return null;
  if ((dA > 0) === (dB > 0)) return null;
  let quote = pickQuote(m.symbolA, m.symbolB);
  if (!quote) quote = dA < 0 ? m.symbolA : m.symbolB;
  const isA = quote === m.symbolA;
  const absA = Math.abs(dA), absB = Math.abs(dB);
  const baseAmt = isA ? absB : absA;
  return { price: (isA ? absA : absB) / baseAmt, base: isA ? m.symbolB : m.symbolA, quote, baseAmt };
}

// İki uçlu ortalama fiyat: listedeki EN BÜYÜK artı ile EN BÜYÜK eksi kalem bir
// takas çifti sayılır (biri alınan, biri verilen).
//
// SIRA ÖNEMLİ — önce en büyük ikisi seçilir, süzgeç SONRA o ikisine uygulanır.
// Tersi (önce süzüp sonra seçmek) ölçüldü ve yanlıştı: HELMET grubunda 348 bin
// HELMET'lik baskın bacak süzülüp yerine 1 adetlik CETUS ödülü geçti, fiyat
// 2500 kat saptı.
//
// Büyüklük USD'ye göre — `toArr` listeyi zaten böyle sıralıyor. Adet üzerinden
// sıralamak 4000 adet ucuz reward token'ını 0,5 WBTC'nin önüne koyardı.
// (DİKKAT: USD, pozisyonun `priceA/priceB` alanından geliyor; fiyatı bilinmeyen
// token'da 0 kalır ve sıralama sessizce adede düşer. Bu doğrulama sırasında
// ayrı süreçte başıma geldi — bkz. prices._cache yalnız sunucu sürecinde dolu.)
//
// `gross` verilirse seçilen iki bacak yönlülük sınavından geçmek zorunda: brüt
// akışın yanında önemsiz kalan bacak round-trip artığı ya da tozdur, fiyat
// üretmemeli. Bu sınav olmadan 0,006 USDC + 3e-06 ETH artığı "2295 USDC/ETH"
// gibi inandırıcı ama anlamsız bir sayı üretiyordu.
//
// Tek yönlü listede (hepsi artı ya da hepsi eksi) fiyat YOKTUR: orada takas
// değil saf kâr/zarar var.
// Çift seçimi — TEK tanım: hem Toplam'ın fee dahil fiyatı hem net delta'nın
// ortalama fiyatı buradan geçiyor, yoksa iki satır farklı kurallarla hesaplanırdı.
//
// STABLECOIN KURALI (kullanıcı, 28.10.2026): iki taraf da stabilse çıkan sayı bir
// parite değil stable takas kurudur — "sui-circle roundabout" grubunda
// "0,736 USDC/USDSUI" diye görünüyordu, hiçbir şey ifade etmiyor. O durumda en
// büyük STABİL OLMAYAN kalem esas alınıp karşısına ters işaretli en büyük kalem
// konur; o taraf stabil OLABİLİR ("1 DEEP = X USDSUI" tam istenen fiyattır).
// Her kalem stabilse fiyat yoktur.
function choosePair(items: TokAmt[]): [TokAmt, TokAmt] | null {
  const pos = items.find(t => t.amount > 0);
  const neg = items.find(t => t.amount < 0);
  if (!pos || !neg || !pos.amount || !neg.amount) return null;
  if (!isStable(pos.sym) || !isStable(neg.sym)) return [pos, neg];
  const alt = items.find(t => !isStable(t.sym) && t.amount);
  if (!alt) return null;
  const kars = items.find(t => t.amount && (t.amount > 0) !== (alt.amount > 0));
  if (!kars) return null;
  return alt.amount > 0 ? [alt, kars] : [kars, alt];
}

function pairAvgPrice(items: TokAmt[], gross?: Record<string, number>):
    { price: number; base: string; quote: string } | null {
  const cift = choosePair(items);
  if (!cift) return null;
  const [pos, neg] = cift;
  if (gross) {
    for (const t of [pos, neg]) {
      if (Math.abs(t.amount) <= RESIDUAL_EPS * (gross[t.sym] || 0)) return null;
    }
  }
  const q = pickQuote(pos.sym, neg.sym);
  // Fallback (bilinen quote yok): verilen token quote sayılır — grup ortalamasıyla aynı kural.
  const [qt, bs] = q === pos.sym ? [pos, neg] : [neg, pos];
  return { price: Math.abs(qt.amount) / Math.abs(bs.amount), base: bs.sym, quote: qt.sym };
}

// KAPANMIŞ TURUN AKIŞI ORTALAMA FİYATA GİRMEMELİ (kullanıcı, 01.11.2026)
//
// Round-trip kapandığında ARTIĞI süzülüyordu (bkz. RESIDUAL_EPS) ama KARŞI
// BACAĞI süzülmüyordu: kapanan tur kârını hangi tokende bıraktıysa o token'ın
// netini kaydırıyor. "navi 150 usd" (grup 220) ölçümü:
//   SUI turu +3,4457 USDC kârla kapandı  → SUI neti 0,4642 (eşik 48,82) → süzüldü
//   açık XAGM bacağı −149,3356 USDC verdi
//   net USDC = −145,8899 → fiyat 54,2584 USDC/XAGM (gerçek giriş 55,5399, %2,31 sapma)
// Yani kullanıcının sezdiği doğruydu, mekanizma bir adım ötede: SUI'nin kendisi
// zaten hesaba girmiyor, SUI turunun USDC HASILATI giriyor.
//
// Çözüm: çift seçimi DEĞİŞMEZ (orası ölçülerek oturdu — bkz. choosePair'ın
// üstündeki HELMET notu), yalnız seçilen iki bacağın MİKTARI kapanmış token'a
// dokunan bacaklar hariç yeniden toplanır. Yeniden toplamda çift kurulamazsa
// eski sayıya dönülür → fiyat kaybolmaz.
function netSansClosed(
  members: LpPosition[], swaps: Swap[], kapanmis: Set<string>,
  opts: { fees: boolean; closedOnly: boolean },
): Record<string, number> {
  const m: Record<string, number> = {};
  const add = (k: string, v: number) => { if (v) m[k] = (m[k] || 0) + v; };
  for (const p of members) {
    if (opts.closedOnly && p.status === 'open') continue;
    const dA = p.withdrawA - p.costA, dB = p.withdrawB - p.costB;
    // "Dokunma" sınavı yalnız HAREKET ETMİŞ pair tokenlarına bakar: akış-nötr
    // bacak (163 SUI gir, 163 SUI çık) kapanmış token'ı taşımıyor, yalnız fee
    // üretiyor → düşürülmemeli. Reward sembolleri de sınava girmez; ödül round-trip
    // yapmaz, D'de hiç görünmediği için kapanmis kümesine de düşemez.
    if ((dA !== 0 && kapanmis.has(p.symbolA)) || (dB !== 0 && kapanmis.has(p.symbolB))) continue;
    add(p.symbolA, dA + (opts.fees ? p.feeA : 0));
    add(p.symbolB, dB + (opts.fees ? p.feeB : 0));
    if (opts.fees) {
      for (const r of p.rewards) add(r.symbol, r.amount);
      for (const c of p.paid || []) {
        if (c.kind === 'rent' || c.kind === 'rent_back') continue;   // kira fiyata girmez
        add(c.symbol, -c.amount);
      }
    }
  }
  for (const sw of swaps) {
    if (kapanmis.has(sw.inSymbol) || kapanmis.has(sw.outSymbol)) continue;
    add(sw.inSymbol, -sw.inAmount); add(sw.outSymbol, sw.outAmount);
  }
  return m;
}

function computeGroup(no: number, members: LpPosition[], swaps: Swap[] = []): GroupCalc {
  const px: Record<string, number> = {};   // sembol → canlı USD fiyatı
  const D: Record<string, number> = {}, F: Record<string, number> = {}, R: Record<string, number> = {};
  const C: Record<string, number> = {};    // ödenen masraf (pozitif tutulur, Toplam'dan düşülür)
  const RN: Record<string, number> = {};   // kilitli kira (geri döner → Toplam'a dokunmaz)
  const G: Record<string, number> = {};    // brüt akış (Σ|δ|) — round-trip tespiti için
  const add = (m: Record<string, number>, k: string, v: number) => { if (v) m[k] = (m[k] || 0) + v; };
  for (const p of members) {
    if (p.priceA != null) px[p.symbolA] = p.priceA;
    if (p.priceB != null) px[p.symbolB] = p.priceB;
    const dA = p.withdrawA - p.costA, dB = p.withdrawB - p.costB;
    add(G, p.symbolA, Math.abs(dA)); add(G, p.symbolB, Math.abs(dB));
    add(D, p.symbolA, dA);
    add(D, p.symbolB, dB);
    add(F, p.symbolA, p.feeA); add(F, p.symbolB, p.feeB);
    for (const c of p.paid || []) {
      add(c.kind === 'rent' || c.kind === 'rent_back' ? RN : C, c.symbol, c.amount);
      if (c.amount > 0 && c.usd > 0 && px[c.symbol] === undefined) px[c.symbol] = c.usd / c.amount;
    }
    for (const r of p.rewards) {
      add(R, r.symbol, r.amount);
      if (r.amount > 0 && r.usd > 0 && px[r.symbol] === undefined) px[r.symbol] = r.usd / r.amount;
    }
  }
  // Swap bacakları: grubu LP ile değil takasla kapattığında eksik kalan parça.
  // Delta'ya aynen katılır (çıkan −, giren +) — ort. fiyat/PnL böylece tamamlanır.
  for (const sw of swaps) {
    add(G, sw.inSymbol, Math.abs(sw.inAmount)); add(G, sw.outSymbol, Math.abs(sw.outAmount));
    add(D, sw.inSymbol, -sw.inAmount);
    add(D, sw.outSymbol, sw.outAmount);
  }

  // Açık üyelerin havuzdaki anlık bakiyesi (curA/curB on-chain okumadan gelir).
  const P: Record<string, number> = {};
  for (const p of members) {
    if (p.status !== 'open') continue;
    add(P, p.symbolA, p.curA); add(P, p.symbolB, p.curB);
  }

  const FR: Record<string, number> = { ...F };
  for (const [k, v] of Object.entries(R)) add(FR, k, v);
  const toArr = (m: Record<string, number>): TokAmt[] =>
    Object.entries(m).filter(([, v]) => Math.abs(v) > 1e-9)
      .map(([sym, amount]) => ({ sym, amount, usd: amount * (px[sym] ?? 0) }))
      .sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd) || Math.abs(b.amount) - Math.abs(a.amount));
  const T: Record<string, number> = { ...D };
  for (const [k, v] of Object.entries(FR)) add(T, k, v);
  for (const [k, v] of Object.entries(P)) add(T, k, v);   // havuzdaki sermaye geri sayılır
  for (const [k, v] of Object.entries(C)) add(T, k, -v);  // gaz/launch masrafı
  const E: Record<string, number> = { ...D };
  for (const [k, v] of Object.entries(P)) add(E, k, v);
  const delta = toArr(D), feeReward = toArr(FR), total = toArr(T);
  const pool = toArr(P), exposure = toArr(E);
  const fee = toArr(F), reward = toArr(R), cost = toArr(C);
  const sum = (a: TokAmt[]) => a.reduce((s, t) => s + t.usd, 0);

  // Yön analizi — SADECE hâlâ yönlü duran tokenlar üzerinden.
  // Round-trip kapanmışsa (kalan delta brüt akışın yanında önemsiz) o token yönlü değildir;
  // aksi halde kapanmış bir işlemin artık gürültüsü "ortalama fiyat" gibi görünürdü.
  const directional = delta.filter(t => Math.abs(t.amount) > RESIDUAL_EPS * (G[t.sym] || 0));
  const pos = directional.filter(t => t.amount > 0), neg = directional.filter(t => t.amount < 0);
  // Akışı olup yönlü SAYILMAYAN tokenlar = kapanmış round-trip'ler. Fiyat girdisi
  // bunlara dokunan bacaklardan arındırılır (bkz. netSansClosed).
  const kapanmis = new Set(
    Object.keys(G).filter(s => (G[s] || 0) > 0 && Math.abs(D[s] || 0) <= RESIDUAL_EPS * (G[s] || 0)));
  const duzelt = (p: number | undefined, bs: string | undefined, qt: string | undefined,
                  net: Record<string, number>, ham: Record<string, number>): number | undefined => {
    if (p == null || !bs || !qt || qt === 'USD' || kapanmis.size === 0) return p;
    const b = net[bs], q = net[qt];
    if (!b || !q || (b > 0) === (q > 0)) return p;   // çift kurulamadı → eski sayı
    // YÖN KORUNMALI: arındırma bir takasın MİKTARINI düzeltebilir, YÖNÜNÜ çeviremez.
    // Grup 189'da ölçüldü — orada DEEP yan tur değil, zincirin ARA DURAĞI
    // (900 USDC → 38913 DEEP → 734 SUI → … → 703 SUI). DEEP "kapanmış" sayıldığı
    // için ona dokunan bacaklar düşünce SUI'nin işareti ters döndü ve sayı
    // "850 USDC verip 751 SUI aldım" (1,133) yerine tek bir SUI satışını anlatmaya
    // başladı (1,315). İşaret ters dönüyorsa arındırma yapıyı bozmuş → eski sayı kalır.
    if ((b > 0) !== ((ham[bs] || 0) > 0) || (q > 0) !== ((ham[qt] || 0) > 0)) return p;
    return Math.abs(q) / Math.abs(b);
  };
  const deltaUsd = sum(delta);
  let kind: GroupCalc['kind'];
  let price: number | undefined, base: string | undefined, quote: string | undefined;
  let priceFrom: number | undefined;
  if (pos.length === 1 && neg.length === 1) {
    // açık yönlü takas → ortalama fiyat. Quote önceliği USDC/USDT/SUI.
    // Çift seçimi `choosePair`a devredildi: stablecoin-stablecoin durumunda burada
    // da fiyat üretilmemeli, yoksa Toplam satırı susarken bu satır "0,736 USDC/
    // USDSUI" göstermeye devam ederdi (aynı grup, çelişen iki okuma).
    const cift = choosePair(directional);
    if (cift) {
      const [a, b] = cift;
      const q = pickQuote(a.sym, b.sym);
      const [qt, bs] = q === a.sym ? [a, b] : [b, a];   // fallback: verilen token quote
      quote = qt.sym; base = bs.sym; price = Math.abs(qt.amount) / Math.abs(bs.amount);
      kind = 'price';
    } else kind = deltaUsd >= 0 ? 'profit' : 'loss';
  } else if (pos.length === 1 && neg.length > 1) {
    // Tek token birikmiş ama BİRDEN ÇOK varlıkla ödenmiş (ör. çok-zincirli grup:
    // SUI + SOL verip DEEP almak). Token cinsinden tek fiyat anlamsız → USD bazlı.
    const paidUsd = neg.reduce((sum, t) => sum + Math.abs(t.usd), 0);
    // Her iki taraf da stabilse USD bazlı fiyat da anlamsız ("1 USDC = 1,00 USD").
    const stabilArasi = isStable(pos[0].sym) && neg.every(t => isStable(t.sym));
    if (!stabilArasi && paidUsd > 0 && Math.abs(pos[0].amount) > 0) {
      base = pos[0].sym; quote = 'USD'; price = paidUsd / Math.abs(pos[0].amount);
      kind = 'price';
    } else kind = deltaUsd >= 0 ? 'profit' : 'loss';
  } else if (neg.length === 1 && pos.length > 1) {
    // Simetrik: tek varlık verilip birden çok token alınmış
    const gotUsd = pos.reduce((sum, t) => sum + Math.abs(t.usd), 0);
    const stabilArasi = isStable(neg[0].sym) && pos.every(t => isStable(t.sym));
    if (!stabilArasi && gotUsd > 0 && Math.abs(neg[0].amount) > 0) {
      base = neg[0].sym; quote = 'USD'; price = gotUsd / Math.abs(neg[0].amount);
      kind = 'price';
    } else kind = deltaUsd >= 0 ? 'profit' : 'loss';
  } else if (delta.length === 0) kind = 'mixed';
  else {
    // Grup düzeyinde yönlü token bulunamadı. Bu, gerçek round-trip olabileceği gibi
    // bir token'ın pozisyonlar ARASINDA dolaşmasından da kaynaklanabilir (brüt akış
    // şişer, net küçülür). Üyelerin HEPSİ aynı yönde takas yaptıysa ağırlıklı
    // ortalamaları anlamlı bir fiyattır; yön karışıksa (gerçek round-trip) düşülmez.
    const mp = members.map(rawMemberPrice).filter(Boolean) as
      { price: number; base: string; quote: string; baseAmt: number }[];
    const same = mp.length > 0 && mp.every(x => x.base === mp[0].base && x.quote === mp[0].quote)
      // stablecoin-stablecoin ortalaması da fiyat değildir (aynı kural)
      && !(isStable(mp[0].base) && isStable(mp[0].quote));
    const tot = same ? mp.reduce((acc, x) => acc + x.baseAmt, 0) : 0;
    if (same && tot > 0) {
      base = mp[0].base; quote = mp[0].quote;
      price = mp.reduce((acc, x) => acc + x.price * x.baseAmt, 0) / tot;
      priceFrom = mp.length;   // çift taraflı deposit'ler fiyat üretmez → alt küme olabilir
      kind = 'price';
    } else kind = deltaUsd >= 0 ? 'profit' : 'loss';
  }
  if (kapanmis.size > 0 && kind === 'price') {
    price = duzelt(price, base, quote,
      netSansClosed(members, swaps, kapanmis, { fees: false, closedOnly: false }), D);
  }
  // Fee+reward DAHİL ortalama fiyat — AÇIK ÜYELER HARİÇ (kullanıcı kararı).
  // Açık pozisyonun havuzdaki sermayesi ve henüz claim edilmemiş fee'si her turda
  // değişiyor; onları katmak fiyatı her yenilemede oynatırdı. Burada yalnız
  // gerçekleşmiş akış var → sayı sabittir. Üstteki `price`ın aksine fee, reward ve
  // masraf da dahil: ikisi yan yana "fee ortalamayı nereye çekti"yi gösterir.
  // Swap bacakları dahil — swap tanım gereği gerçekleşmiş bir takastır, pozisyon
  // gibi "açık" kalmaz.
  //
  // Yönlülük eşiği `GA`: TÜM üyelerin (açık dahil) fee/reward/masraf DAHİL brüt
  // akışı. Üç aday ölçüldü, yalnız bu üçünü birden yakalıyor:
  //   • yalnız kapalı üyelerin akışı → açık bacağa karşı toz kalan artığı kaçırır
  //     (bf eth/wbtc borrow: 3e-06 ETH'den "2295 USDC/ETH" üretiyordu),
  //   • yalnız pozisyon akışı (`G`) → fee/reward'la dönen round-trip'i kaçırır
  //     (navi 300 sui: 0,845 SUI artığından "12,5 USDC/SUI"),
  //   • ikisini birden kapsayan `GA` → üçü de susuyor, doğru olanların hiçbiri kaybolmuyor.
  const TC: Record<string, number> = {}, GA: Record<string, number> = {};
  let closedCount = 0;
  for (const p of members) {
    const dA = p.withdrawA - p.costA, dB = p.withdrawB - p.costB;
    add(GA, p.symbolA, Math.abs(dA) + Math.abs(p.feeA));
    add(GA, p.symbolB, Math.abs(dB) + Math.abs(p.feeB));
    for (const r of p.rewards) add(GA, r.symbol, Math.abs(r.amount));
    // Kira HER İKİSİNDEN de dışarıda: fiyat hesabına girmez (kilitli sermaye, geri
    // döner) ve brüt akış ölçüsünü de şişirmemeli — SOL'lu havuzda eşiği yükseltip
    // meşru bir SOL fiyatını bastırırdı.
    for (const c of p.paid || []) {
      if (c.kind === 'rent' || c.kind === 'rent_back') continue;
      add(GA, c.symbol, Math.abs(c.amount));
    }
    if (p.status === 'open') continue;
    closedCount++;
    add(TC, p.symbolA, dA + p.feeA);
    add(TC, p.symbolB, dB + p.feeB);
    for (const r of p.rewards) add(TC, r.symbol, r.amount);
    for (const c of p.paid || []) {
      if (c.kind === 'rent' || c.kind === 'rent_back') continue;
      add(TC, c.symbol, -c.amount);
    }
  }
  for (const sw of swaps) {
    add(TC, sw.inSymbol, -sw.inAmount);
    add(TC, sw.outSymbol, sw.outAmount);
    add(GA, sw.inSymbol, Math.abs(sw.inAmount));
    add(GA, sw.outSymbol, Math.abs(sw.outAmount));
  }
  const tp = closedCount > 0 ? pairAvgPrice(toArr(TC), GA) : null;
  // Aynı arındırma Toplam'ın fee dahil fiyatına da uygulanır — yoksa iki satır
  // farklı tabanla hesaplanıp "fee ortalamayı nereye çekti" okunaksız kalırdı.
  if (tp && kapanmis.size > 0) {
    const d = duzelt(tp.price, tp.base, tp.quote,
      netSansClosed(members, swaps, kapanmis, { fees: true, closedOnly: true }), TC);
    if (d != null) tp.price = d;
  }
  let totalPrice = tp?.price, totalBase = tp?.base, totalQuote = tp?.quote;
  // Üstteki ort. fiyatla AYNI yönde gösterilir: biri "1 WAL = 0,5 SUI", öteki
  // "1 SUI = 2 WAL" çıkarsa iki sayı karşılaştırılamaz. (Aynı token çiftinde
  // pickQuote aynı sonucu verdiği için çevirme hiç tetiklenmez; çift farklıysa
  // ve grubun quote'u bizim tabanımıza düşmüşse tetiklenir.)
  if (tp && kind === 'price' && quote && quote !== 'USD' && tp.quote !== quote && tp.base === quote) {
    totalPrice = 1 / tp.price; totalBase = tp.quote; totalQuote = tp.base;
  }

  return {
    no, rank: members[0]?.groupRank || no, members, delta, feeReward, total, fee, reward,
    deltaUsd, feeRewardUsd: sum(feeReward), totalUsd: sum(total),
    feeUsd: sum(fee), rewardUsd: sum(reward), cost, costUsd: sum(cost),
    rent: toArr(RN), rentUsd: sum(toArr(RN)),
    kind, price, base, quote, priceFrom,
    totalPrice, totalBase, totalQuote, closedCount,
    pool, poolUsd: sum(pool), exposure,
    hasOpen: members.some(m => m.status === 'open'),
    swapCount: swaps.length, swaps,
    status: members[0]?.groupStatus === 'closed' ? 'closed' : 'living',
  };
}

// ── Delta / Hedef ayrımı ──────────────────────────────────────────────────────
// Kullanıcı kurgusu (01.11.2026): bir grup aslında İKİ FARKLI İŞ yapıyor.
//   • DELTA  — çalışma sermayesi havuzda dönüyor: sat, geri al, her turda daha
//              fazla sermaye-token olarak dön. Kazanç BURADA doğuyor.
//   • HEDEF  — biriken kazanç (tur kârı + fee/reward) bir LP/swap ile hedef
//              token'a çevriliyor. Burada kâr DOĞMAZ, yalnız hangi fiyattan
//              bankalandığı belli olur.
// İkisini tek toplamda eritmek üretimi realizasyondan ayırt edilemez kılıyordu:
// "31 Ekim WAL" grubunda net delta −49,48 WAL görünüyor ama bu zarar değil,
// ürettiğinden fazlasını tahsil borusuna sokmuş olmak (farkı fee kapatıyor).
//
// Etiket ELLE veriliyor (pozisyon satırındaki Δ/$ düğmesi). Tahmin YOK: yönü
// işaretten türetmek satış bacağıyla tahsilatı ayırt edemez — ikisi de aynı yönde
// (sermaye → hedef), tek farkları büyüklük. Kullanıcı kararı budur.
//
// Hiçbir satır etiketlenmemişse bu fonksiyon null döner ve kart eskisi gibi kalır.
type Rol = '' | 'delta' | 'hedef';
interface Bacak {
  ts: number; tsEnd: number; d: Record<string, number>; zap: boolean;
}
interface Tur {
  acCap: number; acQuote: number; kapaCap: number; kapaQuote: number;
  bacaklar: Bacak[]; t0: number; t1: number; kapali: boolean;
  satis?: number; alis?: number; kazanc: number;
}
interface GroupSplit {
  capToken: string; capAmount: number; capManual: boolean; capZap: boolean;
  quoteSym: string;
  deltaCount: number; hedefCount: number; untagged: number;
  turlar: Tur[]; kapaliTur: number;
  avgSatis?: number; avgAlis?: number; makas?: number;
  deltaNet: TokAmt[]; turKazanc: number; ortSure: number;
  midTur: TokAmt[];                 // açık tur: sermaye yolun ortasında
  // Açık tur sermayenin yanında cüce kalıyorsa muhtemelen delta değil, yanlışlıkla
  // Δ işaretlenmiş bir TAHSİL bacağıdır. Mutabakat bunu yakalayamıyor (ölçüldü:
  // yanlış kovaya atılan bacak denklemi bozmuyor, yalnız yerini değiştiriyor) —
  // bu yüzden ayrı bir işaret gerekiyor.
  midSuphe: boolean;
  hedefToken: string;
  hedefIn: TokAmt[]; girenKapali: TokAmt[]; hedefOut: number; hedefPrice?: number;
  hedefPool: TokAmt[]; hedefProgress?: number; targetAmount: number;
  recon: { sym: string; hesap: number; kart: number; fark: number }[];
  reconOk: boolean;
  getiri?: number; uretilenCap: number;
  yabanci: string[];
}

function computeSplit(g: GroupCalc, meta: any): GroupSplit | null {
  const rol = (r: string): Rol => (r === 'delta' || r === 'hedef' ? r : '');
  const dBacak = (m: LpPosition): Bacak => ({
    ts: m.openedTs || m.openedAt, tsEnd: m.closedAt || m.openedTs || m.openedAt,
    d: { [m.symbolA]: m.withdrawA - m.costA, [m.symbolB]: m.withdrawB - m.costB },
    zap: !!m.zap,
  });
  const sBacak = (s: Swap): Bacak => ({
    ts: s.ts, tsEnd: s.ts,
    d: { [s.inSymbol]: -s.inAmount, [s.outSymbol]: s.outAmount }, zap: false,
  });
  // Kapalı bacak = gerçekleşmiş akış. Gerçekleşme fiyatı YALNIZ bunlardan kurulur:
  // açık bir tahsil pozisyonu hedef token'ı henüz ÜRETMEDİ, onu böleni şişirmek
  // fiyatı sahte şekilde düşürür (ölçüldü: 0,0073 çıkıyordu, doğrusu 0,0297).
  const deltaB: Bacak[] = [
    ...g.members.filter(m => rol(m.role) === 'delta').map(dBacak),
    ...g.swaps.filter(s => rol(s.role) === 'delta').map(sBacak),
  ].sort((a, b) => a.ts - b.ts);
  const hedefM = g.members.filter(m => rol(m.role) === 'hedef');
  const hedefB: Bacak[] = [
    ...hedefM.map(dBacak),
    ...g.swaps.filter(s => rol(s.role) === 'hedef').map(sBacak),
  ].sort((a, b) => a.ts - b.ts);
  const hedefBKapali: Bacak[] = [
    ...hedefM.filter(m => m.status === 'closed').map(dBacak),
    ...g.swaps.filter(s => rol(s.role) === 'hedef').map(sBacak),   // swap zaten gerçekleşmiştir
  ];
  const untagged = g.members.filter(m => !rol(m.role)).length
    + g.swaps.filter(s => !rol(s.role)).length;
  if (deltaB.length === 0 && hedefB.length === 0) return null;

  const topla = (bs: Bacak[]) => {
    const m: Record<string, number> = {};
    for (const b of bs) for (const [k, v] of Object.entries(b.d)) if (v) m[k] = (m[k] || 0) + v;
    return m;
  };
  const brut = (bs: Bacak[]) => {
    const m: Record<string, number> = {};
    for (const b of bs) for (const [k, v] of Object.entries(b.d)) if (v) m[k] = (m[k] || 0) + Math.abs(v);
    return m;
  };

  // ── Sermaye token'ı ─────────────────────────────────────────────────────────
  // Kural: İLK delta bacağında EKSİ olan token. Sermaye tanım gereği önce çıkar
  // (satılır), sonra geri gelir — yani ilk hareketi eksidir. Brüt büyüklüğe
  // bakmak ayırt edemezdi: bir takasın iki tarafı USD olarak zaten eşittir.
  const dBrut = brut(deltaB);
  const ilk = deltaB[0];
  let capToken = String(meta?.capToken || '');
  const capManual = !!capToken;
  if (!capToken && ilk) {
    const eksi = Object.entries(ilk.d).filter(([, v]) => v < 0)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
    capToken = eksi.length ? eksi[0][0]
      : Object.entries(dBrut).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  }
  // Karşı taraf: delta bacaklarında sermaye dışı en çok akan token.
  const quoteSym = Object.entries(dBrut).filter(([k]) => k !== capToken)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  // ÇORBA UYARISI: tek sermaye token'ı disiplini (kullanıcı kararı, 01.11.2026).
  // Delta bacaklarında üçüncü bir token belirirse grup karışmaya başlamıştır.
  const yabanci = Object.keys(dBrut).filter(k => k !== capToken && k !== quoteSym);

  // ── Turlar ──────────────────────────────────────────────────────────────────
  // `aç` = sermaye çıkıyor (eksi), `kapa` = geri geliyor (artı). Yön İŞARETTEN
  // türetiliyor, kullanıcıya sordurmuyoruz — iki düğme yetiyor.
  // Ardışık aynı yönlü bacaklar TEK bacağa toplanır (kademeli giriş bir turdur).
  const turlar: Tur[] = [];
  const turDisi: Bacak[] = [];   // sermaye token'ı hiç geçmeyen delta bacakları
  let cur: Tur | null = null;
  for (const b of deltaB) {
    const dc = b.d[capToken] || 0;
    const dq = quoteSym ? (b.d[quoteSym] || 0) : 0;
    // Sermaye hareketi olmayan bacak (çok paritELİ grupta başka bir havuzun bacağı)
    // hiçbir tura ait değil ama AKIŞI gerçek: ayrı tutulup gerçekleşen nete katılıyor.
    // Açık tura iliştirseydik mutabakattan düşerdi ve sahte bir fark üretirdi.
    if (!dc) { turDisi.push(b); continue; }
    if (dc < 0) {
      if (cur && cur.kapaCap > 0) { turlar.push(cur); cur = null; }   // önceki tur kapandı
      if (!cur) cur = { acCap: 0, acQuote: 0, kapaCap: 0, kapaQuote: 0, bacaklar: [],
                        t0: b.ts, t1: b.tsEnd, kapali: false, kazanc: 0 };
      cur.acCap += -dc; cur.acQuote += dq;
    } else {
      if (!cur) cur = { acCap: 0, acQuote: 0, kapaCap: 0, kapaQuote: 0, bacaklar: [],
                        t0: b.ts, t1: b.tsEnd, kapali: false, kazanc: 0 };
      cur.kapaCap += dc; cur.kapaQuote += -dq;
      cur.t1 = b.tsEnd;
    }
    cur.bacaklar.push(b);
  }
  if (cur) turlar.push(cur);
  for (const t of turlar) {
    t.kapali = t.acCap > 0 && t.kapaCap > 0;
    t.kazanc = t.kapaCap - t.acCap;
    if (t.acCap > 0 && t.acQuote > 0) t.satis = t.acQuote / t.acCap;
    if (t.kapaCap > 0 && t.kapaQuote > 0) t.alis = t.kapaQuote / t.kapaCap;
  }
  const kapaliTurlar = turlar.filter(t => t.kapali);
  // KAPANMAMIŞ TURLARIN HEPSİ — yalnız ilkini almak akışı kaybediyordu: ölçümde
  // iki bacağı da aynı yöne bakan bir grupta (188) iki kapanmamış tur oluştu ve
  // ikincisinin akışı ne kapalı nete ne tur ortasına girdi; mutabakat 6,1 USDC
  // sahte fark verdi. Her delta bacağı tam olarak bir kovada olmak zorunda.
  const acikTurlar = turlar.filter(t => !t.kapali);
  const acikTur = acikTurlar[0] || null;

  const sAc = kapaliTurlar.reduce((a, t) => a + t.acCap, 0);
  const sAcQ = kapaliTurlar.reduce((a, t) => a + t.acQuote, 0);
  const sKp = kapaliTurlar.reduce((a, t) => a + t.kapaCap, 0);
  const sKpQ = kapaliTurlar.reduce((a, t) => a + t.kapaQuote, 0);
  const avgSatis = sAc > 0 && sAcQ > 0 ? sAcQ / sAc : undefined;
  const avgAlis = sKp > 0 && sKpQ > 0 ? sKpQ / sKp : undefined;
  const makas = avgSatis && avgAlis ? avgSatis / avgAlis - 1 : undefined;
  const ortSure = kapaliTurlar.length
    ? kapaliTurlar.reduce((a, t) => a + Math.max(0, t.t1 - t.t0), 0) / kapaliTurlar.length : 0;

  // Sermaye miktarı: İLK turun `aç` tarafı (kademeli girişte toplamı).
  const capAmount = meta?.capSrc === 'manual' && meta?.capAmount > 0
    ? Number(meta.capAmount)
    : (turlar[0]?.acCap || 0);
  const capZap = !!(ilk?.zap) && meta?.capSrc !== 'manual';

  // ── Hedef bloğu ─────────────────────────────────────────────────────────────
  const hNet = topla(hedefB);
  let hedefToken = String(meta?.targetToken || '');
  if (!hedefToken) {
    const artilar = Object.entries(hNet).filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    if (artilar.length) hedefToken = artilar[0][0];
    else {
      // Henüz hiç çıktı yok (tahsil pozisyonu açık): beslenmeyen tarafı al,
      // stabil olanı tercih et — "neye dönüyorum" sorusunun cevabı odur.
      const aday: string[] = [];
      for (const m of hedefM) for (const s of [m.symbolA, m.symbolB])
        if ((hNet[s] || 0) >= 0 && !aday.includes(s)) aday.push(s);
      hedefToken = aday.find(isStable) || aday[0] || '';
    }
  }
  const hKapali = topla(hedefBKapali);
  const hedefOut = Math.max(0, hKapali[hedefToken] || 0);
  const hedefPool: Record<string, number> = {};
  for (const m of hedefM) {
    if (m.status !== 'open') continue;
    if (m.curA) hedefPool[m.symbolA] = (hedefPool[m.symbolA] || 0) + m.curA;
    if (m.curB) hedefPool[m.symbolB] = (hedefPool[m.symbolB] || 0) + m.curB;
  }

  const px: Record<string, number> = {};
  for (const m of g.members) {
    if (m.priceA != null) px[m.symbolA] = m.priceA;
    if (m.priceB != null) px[m.symbolB] = m.priceB;
  }
  const arr = (m: Record<string, number>): TokAmt[] =>
    Object.entries(m).filter(([, v]) => Math.abs(v) > 1e-9)
      .map(([sym, amount]) => ({ sym, amount, usd: amount * (px[sym] ?? 0) }))
      .sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd) || Math.abs(b.amount) - Math.abs(a.amount));

  const hedefIn = arr(Object.fromEntries(
    Object.entries(hNet).filter(([k, v]) => v < 0 && k !== hedefToken)));
  // Bölen de KAPALI bacaklardan: havuzda bekleyen girdi henüz hiçbir şey üretmedi.
  const girenKapali = arr(Object.fromEntries(
    Object.entries(hKapali).filter(([k, v]) => v < 0 && k !== hedefToken)));
  const hedefPrice = girenKapali.length === 1 && hedefOut > 0
    ? hedefOut / Math.abs(girenKapali[0].amount) : undefined;

  // ── Mutabakat ───────────────────────────────────────────────────────────────
  // Token BAZINDA, fiyat varsayımı YOK → bozulamaz bir denetim.
  //   üretilen = kapalı turların neti + fee/reward − masraf
  //   hesap    = üretilen + hedef neti          (hedefe giren eksi, çıkan artı)
  //   kart     = Toplam − havuzdaki açık bakiye − tur ortasındaki sermaye
  // İkisi tutmazsa bir satır yanlış etiketlenmiştir (ya da etiketsiz kalmıştır).
  const kapaliNet = topla([...kapaliTurlar.flatMap(t => t.bacaklar), ...turDisi]);
  const midNet = topla(acikTurlar.flatMap(t => t.bacaklar));
  const uretilen: Record<string, number> = { ...kapaliNet };
  const ekle = (m: Record<string, number>, k: string, v: number) => { if (v) m[k] = (m[k] || 0) + v; };
  for (const t of g.feeReward) ekle(uretilen, t.sym, t.amount);
  for (const t of g.cost) ekle(uretilen, t.sym, -t.amount);
  const hesapM: Record<string, number> = { ...uretilen };
  for (const [k, v] of Object.entries(hNet)) ekle(hesapM, k, v);
  const kartM: Record<string, number> = {};
  for (const t of g.total) ekle(kartM, t.sym, t.amount);
  for (const t of g.pool) ekle(kartM, t.sym, -t.amount);
  for (const [k, v] of Object.entries(midNet)) ekle(kartM, k, -v);
  const recon = Array.from(new Set([...Object.keys(hesapM), ...Object.keys(kartM)]))
    .map(sym => ({ sym, hesap: hesapM[sym] || 0, kart: kartM[sym] || 0,
                   fark: (hesapM[sym] || 0) - (kartM[sym] || 0) }))
    .filter(r => Math.abs(r.hesap) > 1e-9 || Math.abs(r.kart) > 1e-9 || Math.abs(r.fark) > 1e-9)
    .sort((a, b) => Math.abs(b.fark) - Math.abs(a.fark));
  const reconOk = recon.every(r => Math.abs(r.fark) <= 1e-6 * Math.max(1, Math.abs(r.kart)));

  const uretilenCap = uretilen[capToken] || 0;
  const getiri = capAmount > 0 ? uretilenCap / capAmount : undefined;
  const targetAmount = Number(meta?.targetAmount || 0);

  return {
    capToken, capAmount, capManual, capZap, quoteSym,
    deltaCount: deltaB.length, hedefCount: hedefB.length, untagged,
    turlar, kapaliTur: kapaliTurlar.length,
    avgSatis, avgAlis, makas,
    deltaNet: arr(kapaliNet), turKazanc: kapaliTurlar.reduce((a, t) => a + t.kazanc, 0), ortSure,
    midTur: arr(midNet),
    // Şüphe: ya açık tur sermayenin yanında cüce (yanlış kovaya atılmış tahsil
    // bacağı) ya da birden fazla tur kapanmamış (eşleşme tutmuyor).
    midSuphe: acikTurlar.length > 1
      || (!!acikTur && capAmount > 0 && acikTur.acCap > 0 && acikTur.acCap < 0.2 * capAmount),
    hedefToken, hedefIn, girenKapali, hedefOut, hedefPrice, hedefPool: arr(hedefPool),
    hedefProgress: targetAmount > 0 ? hedefOut / targetAmount : undefined, targetAmount,
    recon, reconOk, getiri, uretilenCap, yabanci,
  };
}

// Swap bacağının kendi fiyatı. Bir swap zaten tanım gereği ters yönlü iki delta'dır
// (bir token çıkar, bir token girer) → fiyat her zaman hesaplanabilir. LP satırlarıyla
// karşılaştırılabilsin diye quote seçimi ve grup yönüne çevirme birebir aynı kuralla.
function swapPrice(sw: Swap, g: GroupCalc): { price: number; base: string; quote: string } | null {
  if (!sw.inAmount || !sw.outAmount) return null;
  let quote = pickQuote(sw.inSymbol, sw.outSymbol) || sw.inSymbol;
  const isIn = quote === sw.inSymbol;
  const baseAmt = isIn ? sw.outAmount : sw.inAmount;
  if (!baseAmt) return null;
  const r = { price: (isIn ? sw.inAmount : sw.outAmount) / baseAmt,
              base: isIn ? sw.outSymbol : sw.inSymbol, quote };
  if (g.kind === 'price' && g.quote && g.quote !== 'USD' && g.base && r.quote !== g.quote
      && (g.quote === sw.inSymbol || g.quote === sw.outSymbol)) {
    return { price: 1 / r.price, base: r.quote, quote: r.base };
  }
  return r;
}

// Tek bir grup üyesinin kendi ortalama fiyatı (yalnız kendi delta'sından).
// Grup fiyat modundaysa AYNI taban/quote ile gösterilir → satırlar birbiriyle ve
// sağdaki grup ortalamasıyla doğrudan karşılaştırılabilir olur.
function memberPrice(m: LpPosition, g: GroupCalc): { price: number; base: string; quote: string } | null {
  const r = rawMemberPrice(m);
  if (!r) return null;
  // Grup fiyat modundaysa satırlar aynı yönde gösterilsin (karşılaştırılabilirlik)
  if (g.kind === 'price' && g.quote && g.quote !== 'USD' && g.base && r.quote !== g.quote
      && (g.quote === m.symbolA || g.quote === m.symbolB)) {
    return { price: 1 / r.price, base: r.quote, quote: r.base };
  }
  return { price: r.price, base: r.base, quote: r.quote };
}

// Funding arb birleşik hesap: LP grubu (spot) + perp bacağı.
// hedgeGap = LP net delta + perp net qty  → 0 ise tam hedge, kalanı yönlü (prop) pozisyon.
// basis    = perp giriş fiyatı ile LP ortalama fiyatı arasındaki fark (uygulama maliyeti).
//            Yalnız LP quote'u dolar-stabil ise anlamlı (SUI cinsinden fiyatla kıyaslanamaz).
const STABLE_QUOTES = ['USDC', 'USDT'];
function arbCalc(g: GroupCalc, leg: PerpLeg) {
  const base = (leg.symbol || '').split('-')[0].toUpperCase();
  // Hedge açığı gerçek maruziyetten ölçülür: açık LP'nin havuzda duran bakiyesi de
  // yönlü pozisyondur. Yalnız gerçekleşen akışa bakmak açık grupta yanlış açık verirdi.
  const lpTok = g.exposure.find(t => t.sym.toUpperCase() === base);
  const lpQty = lpTok ? lpTok.amount : 0;
  const hedgeGap = lpQty + leg.netQty;
  const lpPrice = (g.kind === 'price' && g.base?.toUpperCase() === base) ? g.price! : null;
  const basisPct = (lpPrice && leg.avgEntry && g.quote && STABLE_QUOTES.includes(g.quote.toUpperCase()))
    ? ((leg.avgEntry - lpPrice) / lpPrice) * 100 : null;
  // openPnl: GRUBUN kendi açık miktarı × kendi ortalaması. Borsanın unrealizedPnl'i
  // hesabın tamamı için — grup dışı envanter varsa karışır. Alan yoksa eskiye düş.
  const uPnl = (leg as any).openPnl ?? leg.live?.unrealizedPnl ?? 0;
  // Gerçekleşmiş de grubun kendi işlemlerinden (matchedPnl), borsa bildiriminden değil.
  const rPnl = (leg as any).matchedPnl ?? leg.realizedPnl ?? 0;
  // Net = LP delta + LP fee/reward + funding + perp (gerçekleşmiş + kağıt) − perp komisyon
  const net = g.deltaUsd + g.feeRewardUsd + leg.fundingUsd + rPnl + uPnl - leg.fee;
  return { base, lpQty, hedgeGap, lpPrice, basisPct, uPnl, rPnl, net };
}

// Zincir bazlı explorer linkleri (Sui → SuiVision, Solana → Solscan, Arc → Arc Explorer)
const ARC_POSITION_MANAGER = '0x6049c9a0e26405c0985f9e3685c87d0ae917f82b';
const ARC_V3_MANAGER = '0x39654a85a4c05127f5fd6ed22caec077a0fb1377';
const RHC_V3_MANAGER = '0x73991a25c818bf1f1128deaab1492d45638de0d3';
const RHC_EXPLORER = 'https://robinhoodchain.blockscout.com';
const explorerObj = (chain: string, id: string) => {
  if (chain === 'solana') return `https://solscan.io/account/${id}`;
  if (chain === 'robinhood') {
    const [, ref] = id.split(':');                 // rhc-v3:<tokenId>
    return `${RHC_EXPLORER}/token/${RHC_V3_MANAGER}/instance/${ref}`;
  }
  if (chain === 'arc') {
    // arc-v4:<tokenId> → pozisyon NFT'si · arc-creator:<poolId> → havuz (DexScreener)
    const [kind, ref] = id.split(':');
    if (kind === 'arc-creator') return `https://dexscreener.com/arc/${ref}`;
    const mgr = kind === 'arc-v3' ? ARC_V3_MANAGER : ARC_POSITION_MANAGER;
    return `https://explorer.arc.io/token/${mgr}/instance/${ref}`;
  }
  return `https://suivision.xyz/object/${id}`;
};
// Elle eklenen bacağın zincirde karşılığı yok → link de yok (aksi halde her
// bilinmeyen zincir SuiVision'a düşüp kırık link üretiyordu).
const MANUAL_CHAIN = 'manual';
const explorerTx = (chain: string, tx: string) =>
  chain === 'robinhood' ? `${RHC_EXPLORER}/tx/${tx}`
    : chain === 'solana' ? `https://solscan.io/tx/${tx}`
    : chain === 'arc' ? `https://explorer.arc.io/tx/${tx}`
    : `https://suivision.xyz/txblock/${tx}`;
const EXPLORER_NAME: Record<string, string> = { sui: 'SuiVision', solana: 'Solscan', arc: 'Arc Explorer', robinhood: 'Robinhood Explorer' };
const CHAIN_LABEL: Record<string, string> = { sui: 'Sui', solana: 'Solana', arc: 'Arc', robinhood: 'Robinhood', evm: 'EVM' };
const CHAIN_BADGE: Record<string, string> = {
  solana: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  arc:    'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  robinhood: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};
// Cüzdan çipi için AYRI harita: pozisyon satırında Sui varsayılan zincir olduğu
// için rozetsiz bırakılmıştı, oraya rozet eklemek yüzlerce satırı kalabalıklaştırır.
// Çipte ise üç zincir yan yana duruyor, ayrım şart.
const WALLET_BADGE: Record<string, string> = {
  ...CHAIN_BADGE,
  sui: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  evm: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
};
// Adres biçiminden zincir: Sui 0x+64 hex, EVM (Arc) 0x+40 hex, Solana base58.
// İkisi de 0x ile başladığı için startsWith('0x') Arc'ı Sui sanıyordu.
const isSuiAddr = (w: string) => /^0x[0-9a-fA-F]{64}$/.test(w);
const isEvmAddr = (w: string) => /^0x[0-9a-fA-F]{40}$/.test(w);
// Cüzdan rozeti: AYNI EVM adresi hem Arc'ta hem Robinhood Chain'de kullanılıyor,
// yani adresten zincir çıkarılamaz → 'EVM' denir. Zincir ayrımı pozisyon
// satırlarındaki rozette zaten var.
const walletChain = (w: string) => (isSuiAddr(w) ? 'sui' : isEvmAddr(w) ? 'evm' : 'solana');
// v4 tick → insan okunur fiyat: quote/base (quote önceliği USDC/USDT/SUI)
function tickRange(p: LpPosition): { lo: number; hi: number; base: string; quote: string } | null {
  if (p.tickLower == null || p.tickUpper == null || p.decA == null || p.decB == null) return null;
  // Meteora DLMM'de fiyat adımı bin'dir: (1 + binStep/1e4)^binId. binStep,
  // tickSpacing alanında tutuluyor. Diğer ürünlerde klasik 1,0001^tick.
  const step = p.platform === 'meteora_dlmm' && p.tickSpacing ? 1 + p.tickSpacing / 10000 : 1.0001;
  const px1per0 = (t: number) => Math.pow(step, t) * Math.pow(10, p.decA! - p.decB!);   // B / A
  const q = pickQuote(p.symbolA, p.symbolB) || p.symbolA;
  const a = px1per0(p.tickLower), b = px1per0(p.tickUpper);
  // quote A ise fiyat = A per B = 1/(B per A)
  const [lo, hi] = q === p.symbolA ? [1 / b, 1 / a] : [a, b];
  return { lo, hi, base: q === p.symbolA ? p.symbolB : p.symbolA, quote: q };
}

const PLATFORM_LABEL: Record<string, string> = {
  cetus: 'Cetus', momentum: 'Momentum', bluefin: 'Bluefin', turbos: 'Turbos', fullsail: 'Full Sail',
  cetus_dlmm: 'Cetus', uniswap_v4: 'Uniswap v4', uniswap_v3: 'Uniswap v3',
  meteora_dlmm: 'Meteora DLMM', meteora_damm2: 'Meteora DAMM v2', arc_creator: 'Creator',
};
// DLMM = bin tabanlı ürün (CLMM'den ayrı) → kartta ayrı rozetle gösterilir
const DLMM_PLATFORMS = new Set(['cetus_dlmm', 'meteora_dlmm']);
const KIND_LABEL: Record<string, string> = {
  deposit: 'Deposit', withdraw: 'Withdraw', collect_fee: 'Fee', collect_reward: 'Reward',
  gas: 'Gaz', launch_fee: 'Launch',
  // Solana'da pozisyon hesabının kirası kapanışta iade edilir → ikisi ayrı kalem
  rent: 'Kira (kilitli)', rent_back: 'Kira iadesi',
};
const kindColor = (k: string, dark: boolean) => {
  switch (k) {
    case 'deposit':        return dark ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-700 bg-emerald-100';
    case 'withdraw':       return dark ? 'text-rose-400 bg-rose-500/10'       : 'text-rose-700 bg-rose-100';
    case 'collect_fee':    return dark ? 'text-sky-400 bg-sky-500/10'         : 'text-sky-700 bg-sky-100';
    case 'collect_reward': return dark ? 'text-violet-400 bg-violet-500/10'   : 'text-violet-700 bg-violet-100';
    case 'gas':
    case 'launch_fee':     return dark ? 'text-orange-400 bg-orange-500/10'   : 'text-orange-700 bg-orange-100';
    default:               return dark ? 'text-slate-400 bg-slate-500/10'     : 'text-slate-600 bg-slate-100';
  }
};

interface Props { dark?: boolean }

export default function LpTrax({ dark = true }: Props) {
  const { sendWs, subscribe, connected } = useWS() as any;

  const [wallets, setWallets]   = useState<string[]>([]);
  // Cüzdanın GÖRÜNEN adı — sunucuda saklanır (bildirimler de aynı adı kullanıyor).
  const [walletNames, setWalletNames] = useState<Record<string, string>>({});
  const [editWallet, setEditWallet]   = useState('');   // adı düzenlenen cüzdan
  const [editName, setEditName]       = useState('');
  const [copied, setCopied]           = useState('');
  const [wallet, setWallet]     = useState('');
  const [input, setInput]       = useState('');
  const [positions, setPositions] = useState<LpPosition[]>([]);
  const [eventsMap, setEventsMap] = useState<Record<string, LpEvent[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [syncing, setSyncing]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [banner, setBanner]     = useState('');
  const [filteredIds, setFilteredIds] = useState<Set<string> | null>(null);
  const [platformF, setPlatformF] = useState('all');
  // Zincir filtresi: aynı cüzdan birçok ağda kullanılıyor, tek tek aramak verimsiz
  const [chainF, setChainF] = useState('all');
  const [tokenF, setTokenF]       = useState<Set<string>>(new Set());
  const [tokenMenuOpen, setTokenMenuOpen] = useState(false);
  const tokenMenuRef = useRef<HTMLDivElement>(null);
  const [favOnly, setFavOnly]     = useState(false);
  const [tab, setTab]             = useState<'open' | 'closed' | 'groups' | 'swaps'>('open');
  const [swaps, setSwaps]         = useState<Swap[]>([]);
  const [swapTotal, setSwapTotal] = useState(0);
  const [swapSel, setSwapSel]     = useState<Set<string>>(new Set());
  const [addSwap, setAddSwap]     = useState(false);   // Sui: elle tx ekleme popup'ı
  const [elleSwap, setElleSwap]   = useState(false);   // zincir dışı (borsa) bacak popup'ı
  const [exitPos, setExitPos]     = useState<LpPosition | null>(null);  // Arc v4: arayüzsüz çekim
  const [swapRole, setSwapRole]   = useState('');
  const [groupSwaps, setGroupSwaps] = useState<Record<string, Swap[]>>({});
  // Sekme başına AYRI veri: tüm geçmişi (4000+ pozisyon, ~4.3 MB) her yüklemede
  // göndermek sayfayı yavaşlatıyordu. Artık sunucu filtreliyor/sayfalıyor.
  const [overview, setOverview]       = useState<any>(null);   // portföy özeti (sunucu)
  const [groupPositions, setGroupPos] = useState<LpPosition[]>([]);  // grup üyeleri (durumdan bağımsız)
  const [hasMore, setHasMore]         = useState(false);       // kapalı sayfada devamı var mı
  const [closedOffset, setClosedOffset] = useState(0);         // sunucu offset'i (arama listeyi ezebiliyor)
  const [allGroups, setAllGroups]     = useState<any[]>([]);   // grup listesi (atama select'i için)
  const [editGroup, setEditGroup]     = useState<number>(0);   // adı düzenlenen grup (0 = yok)
  const [editGroupName, setEditGroupName] = useState('');
  const [dateFrom, setDateFrom]   = useState('');   // YYYY-MM-DD (boş = sınırsız)
  const [dateTo, setDateTo]       = useState('');
  const [groupTab, setGroupTab]   = useState<'living' | 'closed'>('living');
  // Rozetten açılan grup pop-up'ı (null = kapalı). `rank` de taşınıyor: grup
  // üyeleri henüz yüklenmemişken başlıkta ham `no`yu göstermek rozetteki
  // numaradan farklı çıkıyordu.
  const [groupModal, setGroupModal] = useState<{ no: number; rank: number } | null>(null);
  // Sermaye / hedef miktarı satır içi düzenleme (grup no = açık olan kart)
  const [capEdit, setCapEdit] = useState(0);
  const [capAmtIn, setCapAmtIn] = useState('');
  const [capTokIn, setCapTokIn] = useState('');
  const [tgtEdit, setTgtEdit] = useState(0);
  const [tgtAmtIn, setTgtAmtIn] = useState('');
  const [tgtTokIn, setTgtTokIn] = useState('');
  // Kademeli render: 4000+ kartı birden DOM'a basmak sayfayı kilitliyor.
  // İlk PAGE kadar basılır, kullanıcı listenin sonuna gelince otomatik büyür.
  const PAGE = 60;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const moreRef = useRef<HTMLDivElement>(null);
  const [perpLegs, setPerpLegs]   = useState<Record<string, PerpLeg>>({});
  const [showAuth, setShowAuth]   = useState(false);   // Bluefin yetkilendirme paneli (tek seferlik iş)
  // Yetkilendirilecek adres LP cüzdan listesinden BAĞIMSIZ: zkLogin cüzdanı LP
  // tutmuyor olabilir (Vera böyle). Listeye eklemek LP Trax'i alakasız satırlarla
  // kirletirdi; adres burada elle giriliyor.
  const [authAddr, setAuthAddr]   = useState('');

  // Sekme başlığı — sayfadan çıkınca eski başlığa dön
  useEffect(() => {
    const prev = document.title;
    document.title = 'Timur Bey LP Trax';
    return () => { document.title = prev; };
  }, []);

  // Aktif cüzdanın güncel değeri (WS handler'ları bayat closure görmesin)
  const walletRef = useRef('');
  const walletsInitRef = useRef(false);   // cüzdan listesi ilk kez geldi mi
  // Banner'lar abonelik closure'ı içinde çalışıyor; isim haritasını ref'ten okur
  // (state doğrudan okunsaydı ilk render'daki boş harita kalırdı).
  const walletLabelRef = useRef((w: string) => shortId(w));
  const tabRef = useRef<'open' | 'closed' | 'groups' | 'swaps'>('open');
  useEffect(() => { walletRef.current = wallet; }, [wallet]);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  // ── Pozisyon iste (DB'den oku, hızlı) ───────────────────────────────────────
  const CLOSED_PAGE = 100;
  const loadPositions = useCallback((w: string, t?: 'open' | 'closed' | 'groups' | 'swaps', off = 0) => {
    if (!w) return;
    const which = t || 'open';
    setLoading(true);
    const payload: any = { wallet: w };
    if (which === 'swaps') {          // swap listesi ayrı uçtan gelir
      setLoading(false);
      sendWs?.({ type: 'get_swaps', payload: { wallet: w, limit: 300 } });
      return;
    }
    if (which === 'groups') payload.groupOnly = true;
    else {
      payload.status = which;                       // 'open' | 'closed'
      if (which === 'closed') { payload.limit = CLOSED_PAGE; payload.offset = off; }
      // Tarih filtresi HER İKİ sekmede de SUNUCUDA — istemcide de süzmek hem çift iş
      // hem de sayfalamayla birlikte yanlış sonuç veriyordu.
      const [f, t2] = rangeToMs(dateFrom, dateTo);
      if (f) payload.fromTs = f;
      if (t2) payload.toTs = t2;
    }
    sendWs?.({ type: 'get_lp_positions', payload });
  }, [sendWs, dateFrom, dateTo]);

  // Portföy özeti — TÜM pozisyonlar üzerinden ama sunucuda hesaplanır (~5 KB)
  const loadOverview = useCallback((w: string) => {
    if (!w) return;
    const [f, t2] = rangeToMs(dateFrom, dateTo);
    sendWs?.({ type: 'get_lp_overview', payload: {
      wallet: w, platform: platformF === 'all' ? '' : platformF,
      tokens: Array.from(tokenF), favOnly, fromTs: f || 0, toTs: t2 || 0,
    } });
  }, [sendWs, dateFrom, dateTo, platformF, tokenF, favOnly]);

  // ── Yenile = ZORLA SYNC: zincirden çek + reconcile + taze sonuç ──────────────
  const forceSync = useCallback((w: string) => {
    if (!w) return;
    setErr(''); setLoading(true);
    sendWs?.({ type: 'lp_sync_wallet', payload: { wallet: w } });
  }, [sendWs]);

  // İlk istek — WS açık/auth'lu olana kadar bekle (sendWs kapalıyken mesajı düşürür)
  useEffect(() => {
    if (connected) sendWs?.({ type: 'get_lp_wallets', payload: {} });
  }, [connected, sendWs]);

  // ── WS abonelik ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribe?.((msg: any) => {
      if (msg.type === 'lp_wallets') {
        const ws: string[] = msg.payload?.wallets ?? [];
        setWallets(ws);
        const nmap: Record<string, string> = msg.payload?.names ?? {};
        setWalletNames(nmap);
        walletLabelRef.current = (w: string) => (nmap[w] ? `${nmap[w]} (${shortId(w)})` : shortId(w));
        // Bu mesaj isim değişiminden sonra da geliyor; ilk açılışta seçim + yükleme
        // yapılır, sonrakilerde SADECE isimler tazelenir (yoksa her yeniden
        // adlandırma tüm pozisyonları boşuna yeniden çekerdi).
        const cur = walletRef.current || ws[0] || '';
        if (cur && !walletsInitRef.current) {
          walletsInitRef.current = true;
          setWallet(cur); loadPositions(cur, 'open'); loadOverview(cur);
          sendWs?.({ type: 'get_lp_trade_tags', payload: {} });
        }
      } else if (msg.type === 'lp_group_created') {
        // Yeni grup açıldı (ve pozisyon ona bağlandı) → listeyi ve görünümü tazele
        sendWs?.({ type: 'get_lp_trade_tags', payload: {} });
        if (walletRef.current) loadPositions(walletRef.current, tabRef.current, 0);
      } else if (msg.type === 'swaps') {
        setSwaps(msg.payload?.swaps ?? []);
        setSwapTotal(msg.payload?.total ?? 0);
      } else if (msg.type === 'group_swaps') {
        setGroupSwaps(msg.payload?.legs ?? {});
      } else if (msg.type === 'manual_swap_saved') {
        // Grup kartları groupSwaps'tan besleniyor; ikisini de tazelemezsek satır
        // listede görünür ama grup matematiğine girmez (yayın vs önbellek tuzağı).
        if (msg.payload?.success === false) setErr(msg.payload?.error || 'elle swap kaydedilemedi');
        sendWs?.({ type: 'get_swaps', payload: { wallet: walletRef.current, limit: 300 } });
        sendWs?.({ type: 'get_group_swaps', payload: {} });
      } else if (msg.type === 'swap_group_saved') {
        setSwapSel(new Set());
        sendWs?.({ type: 'get_swaps', payload: { wallet: walletRef.current, limit: 300 } });
        sendWs?.({ type: 'get_group_swaps', payload: {} });
      } else if (msg.type === 'lp_group_status_saved') {
        // İyimser güncelleme ekranı anında taşıdı; burada sunucu gerçeğiyle doğruluyoruz
        // (yazma sessizce başarısız olsaydı ekran yalan söylemeye devam ederdi).
        if (!msg.payload?.success) { setErr('grup durumu kaydedilemedi'); }
        if (tabRef.current === 'groups' && walletRef.current) loadPositions(walletRef.current, 'groups', 0);
      } else if (msg.type === 'lp_trade_tags') {
        setAllGroups(msg.payload?.groups || []);
      } else if (msg.type === 'lp_overview') {
        const forWallet = msg.payload?.wallet || '';
        if (forWallet && walletRef.current && forWallet !== walletRef.current) return;
        setOverview(msg.payload ?? null);
      } else if (msg.type === 'lp_positions') {
        // Bayat yanıt koruması: başka cüzdana ait geç gelen liste aktif görünümü ezmesin
        const forWallet = msg.payload?.wallet || '';
        if (forWallet && walletRef.current && forWallet !== walletRef.current) return;
        setLoading(false);
        const rows: LpPosition[] = msg.payload?.positions ?? [];
        if (msg.payload?.groupOnly) {
          setGroupPos(rows);                       // grup sekmesi kendi setini tutar
        } else {
          const off = Number(msg.payload?.offset || 0);
          const lim = Number(msg.payload?.limit || 0);
          // offset>0 → sayfa EKLENİR (kapalı listesinde "daha fazla")
          setPositions(prev => (off > 0 ? [...prev, ...rows] : rows));
          setHasMore(lim > 0 && rows.length >= lim);
          setClosedOffset(off + rows.length);
        }
        // Perp bacakları yalnız Gruplar sekmesinde gerekiyor — her sayfa yüklemesinde
        // istemek gereksiz trafikti.
        if (msg.payload?.groupOnly) {
          sendWs?.({ type: 'get_lp_group_perp', payload: {} });
          sendWs?.({ type: 'get_group_swaps', payload: {} });
        }
      } else if (msg.type === 'lp_group_perp') {
        setPerpLegs(msg.payload?.legs ?? {});
      } else if (msg.type === 'lp_position_events') {
        setEventsMap(prev => ({ ...prev, [msg.payload?.positionId]: msg.payload?.events ?? [] }));
      } else if (msg.type === 'lp_sync_result') {
        setSyncing(false); setLoading(false);
        if (!msg.payload?.success) { setErr(msg.payload?.error || 'sync hatası'); return; }
        // Sunucu artık sync sonunda liste PUSH'lamıyor (sayfa ayrımını bozuyordu).
        // Taze veriyi aktif sekme için biz isteriz — Swaplar sekmesi de böylece tazelenir.
        const w = walletRef.current;
        if (w) {
          setPositions([]); setClosedOffset(0);
          loadPositions(w, tabRef.current, 0);
          loadOverview(w);
          sendWs?.({ type: 'get_lp_trade_tags', payload: {} });
          if (tabRef.current !== 'swaps') sendWs?.({ type: 'get_swaps', payload: { wallet: w, limit: 300 } });
        }
      } else if (msg.type === 'lp_lookup_result') {
        setSyncing(false);
        const pl = msg.payload || {};
        const found: LpPosition[] = pl.positions ?? [];
        if (pl.kind === 'error') { setErr(pl.error || 'arama hatası'); return; }
        if (found.length === 0) { setErr('Sonuç bulunamadı'); setBanner(''); return; }
        setErr('');
        if (pl.wallet) { walletRef.current = pl.wallet; setWallet(pl.wallet); }
        setPositions(found);
        setEventsMap(pl.events ?? {});
        if (pl.kind === 'wallet') {
          setFilteredIds(null); setExpanded(new Set());
          setBanner(`Cüzdan ${walletLabelRef.current(pl.wallet)} → ${found.length} pozisyon`);
        } else {
          const ids = new Set(found.map(p => p.positionId));
          setFilteredIds(ids); setExpanded(ids);   // otomatik aç
          const kindLbl = pl.kind === 'tx' ? 'TX' : 'Position';
          setBanner(`${kindLbl} ${shortId(pl.query)} → ${found.length} pozisyon (cüzdan ${walletLabelRef.current(pl.wallet)})`);
        }
      } else if (msg.type === 'lp_updated') {
        if (walletRef.current) {
          loadPositions(walletRef.current, tabRef.current, 0);
          loadOverview(walletRef.current);
        }
      }
    });
    return () => unsub?.();
  }, [subscribe, sendWs, loadPositions, loadOverview]);

  // ── Cüzdan değiştir / yapıştır ──────────────────────────────────────────────
  // Cüzdan adı yalnız ETİKET: sunucuda saklanır, cüzdan listesini değiştirmez.
  // Boş ad kaydı siler → arayüz otomatik kısa adrese döner.
  const saveWalletName = (w: string) => {
    const nm = editName.trim();
    setEditWallet('');
    if (nm === (walletNames[w] || '')) return;
    setWalletNames(prev => { const n = { ...prev }; if (nm) n[w] = nm; else delete n[w]; return n; });
    sendWs?.({ type: 'set_lp_wallet_name', payload: { wallet: w, name: nm } });
  };

  // Grup adı yalnız ETİKET: `group_no` iç anahtar olarak kalıyor. Boş ad kaydı
  // siler → etiket "Grup N"e döner (cüzdan adlarıyla aynı sözleşme).
  const groupMeta = (no: number) => allGroups.find((x: any) => x.no === no);

  const startEditGroup = (g: any) => {
    const meta = groupMeta(g.no);
    setEditGroup(g.no);
    // Adsızsa parite ile ÖN DOLDUR — tek tuşla onaylansın, grup adsız kalmasın.
    setEditGroupName((meta?.name || '') || groupNameSuggestion(g.members?.[0]));
  };

  const saveGroupName = (no: number) => {
    const nm = editGroupName.trim();
    const cur = (groupMeta(no)?.name || '');
    setEditGroup(0);
    if (nm === cur) return;
    // İyimser güncelleme: sunucu zaten tüm listeyi geri yolluyor, ama kart
    // tıklamadan hemen sonra yeni adı göstersin.
    setAllGroups(prev => prev.map((x: any) => (x.no === no ? { ...x, name: nm } : x)));
    sendWs?.({ type: 'set_lp_group_name', payload: { groupNo: no, name: nm } });
  };

  const copyWallet = (w: string) => {
    navigator.clipboard?.writeText(w).then(() => {
      setCopied(w);
      setTimeout(() => setCopied(c => (c === w ? '' : c)), 1500);
    }).catch(() => { /* pano yoksa sessiz geç */ });
  };

  const selectWallet = (w: string) => {
    setChainF('all');
    setErr(''); setBanner(''); setFilteredIds(null); setEventsMap({}); setExpanded(new Set());
    setTokenF(new Set());   // token filtresini de sıfırla
    walletRef.current = w;   // bayat yanıt korumasını anında güncelle
    setWallet(w);
    // Aktif sekmeyi yükle (argümansız çağrı 'open'a düşüp yanlış liste getiriyordu)
    // ve özeti tazele (yoksa sekme sayaçları önceki cüzdandan kalıyordu).
    setPositions([]); setHasMore(false); setClosedOffset(0);
    loadPositions(w, tabRef.current, 0); loadOverview(w);
  };
  const clearLookup = () => {
    setBanner(''); setFilteredIds(null); setInput(''); setExpanded(new Set());
    setPositions([]); setHasMore(false); setClosedOffset(0);
    if (wallet) { loadPositions(wallet, tabRef.current, 0); loadOverview(wallet); }
  };
  // Birleşik arama: cüzdan / position id / tx id
  const runLookup = () => {
    const q = input.trim();
    if (!q) return;
    setErr(''); setSyncing(true);
    sendWs?.({ type: 'lp_lookup', payload: { query: q } });
  };

  // ── Kart aç/kapa (+ event defterini tembel yükle) ───────────────────────────
  const toggle = (pid: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else { next.add(pid); if (!eventsMap[pid]) sendWs?.({ type: 'get_lp_position_events', payload: { positionId: pid } }); }
      return next;
    });
  };

  // Favori/not kaydet — optimistic (anında UI) + WS'e gönder
  const setAnnotation = useCallback((positionId: string, patch: { favorite?: boolean; note?: string; groupNo?: number; role?: string }) => {
    setPositions(prev => prev.map(p => p.positionId === positionId ? { ...p, ...patch } : p));
    sendWs?.({ type: 'set_lp_annotation', payload: { positionId, ...patch } });
  }, [sendWs]);

  // Grubu yaşayan/kapalı etiketle — optimistic (grubun tüm üyelerine yansıt) + WS'e gönder.
  // groupPositions'ı da güncellemek ŞART: Gruplar sekmesi o setten besleniyor, yalnız
  // positions'a yazınca kart Yaşayan↔Kapalı arasında hiç yer değiştirmiyordu.
  const setGroupStatus = useCallback((groupNo: number, status: 'living' | 'closed') => {
    const patch = (p: LpPosition) => (p.groupNo === groupNo ? { ...p, groupStatus: status } : p);
    setPositions(prev => prev.map(patch));
    setGroupPos(prev => prev.map(patch));
    sendWs?.({ type: 'set_lp_group_status', payload: { groupNo, status } });
  }, [sendWs]);

  // ── Grup pop-up'ı ───────────────────────────────────────────────────────────
  // Kart `groupPositions`tan besleniyor; o set yalnız Gruplar sekmesinde
  // dolduruluyordu. Pop-up başka sekmeden de açıldığı için üyeleri burada
  // istiyoruz — yanıt handler'ı perp bacaklarını ve swapları da zincirleme çekiyor.
  const openGroupModal = useCallback((no: number, rank: number) => {
    setGroupModal({ no, rank });
    if (walletRef.current) {
      sendWs?.({ type: 'get_lp_positions', payload: { wallet: walletRef.current, groupOnly: true } });
    }
  }, [sendWs]);

  // Esc ile kapat — ad düzenleme açıkken Esc ÖNCE alanı iptal etsin (yoksa tek
  // tuşla hem düzenleme hem pop-up kapanıyordu).
  useEffect(() => {
    if (!groupModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editGroup) return;
      setGroupModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [groupModal, editGroup]);

  // Tarih filtresi: pozisyonun AÇILIŞ tarihine göre (openedTs = opened_at, yoksa ilk event)
  const [fromMs, toMs] = useMemo(() => rangeToMs(dateFrom, dateTo), [dateFrom, dateTo]);

  const filtered = useMemo(() => positions.filter(p =>
    (platformF === 'all' || p.platform === platformF) &&
    (chainF === 'all' || (p.chain || 'sui') === chainF) &&
    (!filteredIds || filteredIds.has(p.positionId)) &&
    (tokenF.size === 0 || tokenF.has(p.symbolA) || tokenF.has(p.symbolB)) &&
    (!favOnly || p.favorite)
    // Tarih filtresi burada YOK: sunucuda uygulanıyor (bkz. loadPositions)
  ), [positions, platformF, chainF, filteredIds, tokenF, favOnly]);

  // Filtre listeleri ÖZETTEN gelir (tüm cüzdan). Yüklü sekmeden türetilirse
  // Açık sekmesinde 0 pozisyon varken dropdown'lar boş kalıyordu.
  const platforms = useMemo(
    () => (overview?.allPlatforms as string[]) ?? Array.from(new Set(positions.map(p => p.platform))),
    [overview, positions]);
  // Zincirler ÖZETTEN gelir: Açık sekmesinde o zincirde pozisyon yoksa bile düğme durur
  const chains = useMemo(
    () => (overview?.allChains as string[]) ?? Array.from(new Set(positions.map(p => p.chain || 'sui'))),
    [overview, positions]);
  // Pozisyonlarda geçen token sembolleri (dropdown için)
  const tokens = useMemo(() => {
    if (overview?.allTokens) return overview.allTokens as string[];
    const s = new Set<string>();
    positions.forEach(p => { if (p.symbolA) s.add(p.symbolA); if (p.symbolB) s.add(p.symbolB); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [overview, positions]);
  const toggleToken = (sym: string) => setTokenF(prev => {
    const next = new Set(prev);
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    return next;
  });

  // Portföy özeti — filtrelenmiş sete göre (fee/reward token bazında toplanır)
  // Portföy özeti artık SUNUCUDA hesaplanıyor (get_lp_overview): tüm pozisyonları
  // tarayıcıya göndermeden doğru toplamlar. Boşsa sıfırlarla render edilir.
  const summary = useMemo<any>(() => overview ?? {
    open: 0, closed: 0, total: 0, plats: [] as [string, number][],
    feeUsd: 0, rewardUsd: 0, tvlUsd: 0, earnedUsd: 0,
    tvlTokens: [], feeTokens: [], rewardTokens: [], earnedTokens: [],
  }, [overview]);

  // Filtre/sekme değişince baştan başla (aksi halde 4000 kart açık kalır)
  useEffect(() => { setVisibleCount(PAGE); }, [platformF, chainF, tokenF, favOnly, fromMs, toMs, filteredIds, wallet, tab]);

  // Filtre değişince özeti tazele (tile'lar listeyle aynı kapsamı göstersin).
  // Liste zaten istemcide süzülüyor; tarih ise sunucuda → onu ayrıca yeniden çek.
  const filtersReady = useRef(false);
  useEffect(() => {
    if (!filtersReady.current) { filtersReady.current = true; return; }
    const w = walletRef.current;
    if (!w) return;
    const id = setTimeout(() => {
      loadOverview(w);
      if (dateFrom || dateTo) { setPositions([]); setClosedOffset(0); loadPositions(w, tabRef.current, 0); }
    }, 250);
    return () => clearTimeout(id);
  }, [platformF, tokenF, favOnly, dateFrom, dateTo]);   // eslint-disable-line

  // Listenin sonundaki sentinel görününce bir sayfa daha bas
  useEffect(() => {
    if (tab === 'groups') return;
    const el = moreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisibleCount(c => (c < filtered.length ? c + PAGE : c));
    }, { rootMargin: '600px' });   // ekrana girmeden önce yükle → kesintisiz kaydırma
    io.observe(el);
    return () => io.disconnect();
  }, [tab, filtered.length, visibleCount]);

  // Delta grupları — TÜM pozisyonlardan (filtreden bağımsız), group_no > 0 olanlar.
  const groups = useMemo(() => {
    const by: Record<number, LpPosition[]> = {};
    for (const p of groupPositions) if (p.groupNo > 0) (by[p.groupNo] ||= []).push(p);
    return Object.entries(by)
      .map(([no, members]) => {
        const g = computeGroup(Number(no), members.sort((a, b) => a.seq - b.seq), groupSwaps[no] || []);
        // Ayrım AYRI adımda: computeGroup saf kalsın, hesabını grup meta'sı (sermaye,
        // hedef token) değiştirmesin. Mutabakat zaten g.total'a karşı doğrulanıyor.
        g.split = computeSplit(g, groupMeta(Number(no)));
        return g;
      })
      .sort((a, b) => b.no - a.no);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupPositions, groupSwaps, allGroups]);

  // Kapalı grupların toplamı — gerçekleşmiş sonuç (ana sayfa özet şeridiyle aynı biçim)
  const closedSummary = useMemo(() => {
    const closed = groups.filter(g => g.status === 'closed');
    const D: Record<string, number> = {}, F: Record<string, number> = {}, R: Record<string, number> = {}, T: Record<string, number> = {};
    let deltaUsd = 0, feeUsd = 0, rewardUsd = 0, totalUsd = 0;
    const add = (m: Record<string, number>, k: string, v: number) => { if (v) m[k] = (m[k] || 0) + v; };
    for (const g of closed) {
      for (const t of g.delta)  add(D, t.sym, t.amount);
      for (const t of g.fee)    add(F, t.sym, t.amount);
      for (const t of g.reward) add(R, t.sym, t.amount);
      for (const t of g.total)  add(T, t.sym, t.amount);
      deltaUsd += g.deltaUsd; feeUsd += g.feeUsd; rewardUsd += g.rewardUsd; totalUsd += g.totalUsd;
    }
    const toArr = (m: Record<string, number>) =>
      Object.entries(m).filter(([, v]) => Math.abs(v) > 1e-9)
        .map(([sym, amount]) => ({ sym, amount }))
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    return {
      count: closed.length,
      deltaTokens: toArr(D), feeTokens: toArr(F), rewardTokens: toArr(R), totalTokens: toArr(T),
      deltaUsd, feeUsd, rewardUsd, totalUsd,
    };
  }, [groups]);

  // ── Grup meta (sermaye / hedef) ─────────────────────────────────────────────
  // Ad kaydetmekle aynı sözleşme: iyimser güncelle, sunucu tüm listeyi geri yollar.
  const saveGroupMeta = (no: number, patch: any) => {
    setAllGroups(prev => prev.map((x: any) => (x.no === no ? { ...x, ...patch } : x)));
    sendWs?.({ type: 'set_lp_group_meta', payload: { groupNo: no, ...patch } });
  };
  const saveCap = (no: number) => {
    setCapEdit(0);
    if (!capAmtIn.trim()) { saveGroupMeta(no, { capSrc: '', capAmount: 0, capToken: '' }); return; }
    const amt = parseFloat(capAmtIn.replace(',', '.'));
    if (!isFinite(amt) || amt <= 0) return;
    saveGroupMeta(no, { capSrc: 'manual', capAmount: amt, capToken: capTokIn.trim().toUpperCase() });
  };
  const saveTgt = (no: number) => {
    setTgtEdit(0);
    const amt = parseFloat(tgtAmtIn.replace(',', '.'));
    saveGroupMeta(no, {
      targetToken: tgtTokIn.trim().toUpperCase(),
      targetAmount: isFinite(amt) && amt > 0 ? amt : 0,
    });
  };

  // Satır rolü: Δ (delta turu) / $ (hedefe tahsil). Üçüncü düğme YOK — aktif olana
  // tekrar basmak etiketi kaldırıyor. Delta'nın aç/kapa yönü İŞARETTEN türüyor,
  // kullanıcıya sordurulmuyor (sermaye token'ı eksiyse satılmış, artıysa geri alınmış).
  const rolSec = (cur: string, set: (r: string) => void, g: GroupCalc, m?: LpPosition) => {
    const sp = g.split;
    let yon = '';
    if (cur === 'delta' && sp?.capToken && m) {
      const d = m.symbolA === sp.capToken ? m.withdrawA - m.costA
        : m.symbolB === sp.capToken ? m.withdrawB - m.costB : 0;
      yon = d < 0 ? 'sat' : d > 0 ? 'al' : '';
    }
    const tus = (r: 'delta' | 'hedef', etiket: string, aktif: string, baslik: string) => (
      <button onClick={e => { e.stopPropagation(); set(cur === r ? '' : r); }} title={baslik}
        className={`px-1.5 py-0.5 text-[10px] leading-none transition ${
          cur === r ? aktif : `${muted} hover:text-sky-400`}`}>
        {etiket}
      </button>
    );
    return (
      <span className={`flex shrink-0 rounded border overflow-hidden ${sub}`}>
        {tus('delta', yon ? `Δ ${yon}` : 'Δ', 'bg-sky-500/20 text-sky-400 font-semibold',
          'Delta turu — çalışma sermayesinin satılıp geri alındığı bacak. Kazanç burada doğar. Yön (sat/al) işaretten türer. Tekrar tıkla = etiketi kaldır.')}
        {tus('hedef', '$', 'bg-emerald-500/20 text-emerald-400 font-semibold',
          'Hedefe tahsil — kazancın hedef token’a çevrildiği bacak. Burada kâr doğmaz; yalnız hangi fiyattan bankalandığı belli olur.')}
        {(cur === 'open' || cur === 'closing') && (
          <span className={`px-1 text-[9px] leading-none self-center ${muted}`}
            title={`eski etiket: ${cur} — yeni hesaba girmiyor, Δ/$ ile değiştir`}>·{cur[0]}</span>
        )}
      </span>
    );
  };

  // ── Grup kartı (tek gövde) ──────────────────────────────────────────────────
  // Aynı kart iki yerde basılıyor: Gruplar sekmesindeki liste ve pozisyon
  // rozetinden açılan pop-up. İki kopya tutmak, hesapların zamanla birbirinden
  // sapmasına açık kapı bırakırdı.
  const renderGroupCard = (g: GroupCalc) => {
              // Perp bacağı varsa toplamlara dahil edilir (spot + perp tek sonuç)
              const leg = perpLegs[String(g.no)];
              const arb = leg ? arbCalc(g, leg) : null;
              return (
              <div key={g.no} className={`rounded-xl border ${card} p-3.5`}>
                <div className="flex items-start gap-4">
                  {/* Sol: grup başlığı + üyeler */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {editGroup === g.no ? (
                        <input autoFocus value={editGroupName}
                          onChange={e => setEditGroupName(e.target.value)}
                          onBlur={() => saveGroupName(g.no)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveGroupName(g.no);
                            if (e.key === 'Escape') setEditGroup(0);
                          }}
                          placeholder={`Grup ${g.rank}`}
                          className={`text-sm font-semibold bg-transparent border-b border-sky-500/60 outline-none w-44 ${text}`} />
                      ) : (
                        <span className="text-sm font-semibold flex items-center gap-1.5 cursor-text hover:text-sky-400 transition-colors"
                          onClick={() => startEditGroup(g)}
                          title={groupTitle(groupMeta(g.no), g.rank) || 'Ad vermek için tıkla'}>
                          <Layers size={14} className="text-sky-400" />
                          {groupLabel(groupMeta(g.no), g.rank)}
                        </span>
                      )}
                      <span className={`text-[11px] ${muted}`}>{g.members.length} pozisyon</span>
                      {g.hasOpen && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">açık üye var</span>}
                      {g.swapCount > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/30"
                          title="Delta'ya dahil edilen swap bacağı">{g.swapCount} swap</span>
                      )}
                      <button
                        onClick={() => setGroupStatus(g.no, g.status === 'closed' ? 'living' : 'closed')}
                        title={g.status === 'closed' ? 'Grubu tekrar yaşayan yap' : 'Grubu kapalı olarak etiketle'}
                        className={`ml-auto text-[11px] px-2.5 py-1 rounded-lg border transition ${g.status === 'closed'
                          ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'
                          : `${sub} ${muted} hover:text-sky-400 hover:border-sky-500/40`}`}>
                        {g.status === 'closed' ? 'Aç' : 'Kapat'}
                      </button>
                    </div>
                    <div className="space-y-1">
                      {g.members.map(m => (
                        <div key={m.positionId} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${sub} border`}>
                          <span className={`font-mono tabular-nums ${muted} w-12 shrink-0`}>#{m.seq}</span>
                          <span className="font-medium shrink-0">{m.symbolA}/{m.symbolB}</span>
                          {rolSec(m.role, r => setAnnotation(m.positionId, { role: r }), g, m)}
                          <span className={`flex-1 min-w-0 truncate ${muted}`}>
                            {fmtSigned(m.withdrawA - m.costA)} {m.symbolA} · {fmtSigned(m.withdrawB - m.costB)} {m.symbolB}
                          </span>
                          {(() => {
                            const mp = memberPrice(m, g);
                            return mp ? (
                              <span className="shrink-0 tabular-nums flex items-center gap-1" title={`1 ${mp.base} = ${fmtPrice(mp.price)} ${mp.quote}`}>
                                <Scale size={10} className={muted} />
                                {fmtPrice(mp.price)} <span className={muted}>{mp.quote}/{mp.base}</span>
                              </span>
                            ) : null;
                          })()}
                          <span className={`ml-3 shrink-0 text-[10px] ${muted}`}>{fmtDate(m.openedAt)}</span>
                        </div>
                      ))}
                      {/* Swap bacakları — LP satırlarıyla aynı biçim: ± delta + kendi ort. fiyatı */}
                      {g.swaps.map(sw => {
                        const sp = swapPrice(sw, g);
                        return (
                          <div key={sw.signature}
                            className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${sub} border`}>
                            <span className={`font-mono tabular-nums w-12 shrink-0 ${muted}`}>swap</span>
                            <span className="font-medium shrink-0">{sw.inSymbol}/{sw.outSymbol}</span>
                            {rolSec(sw.role, r => sendWs?.({ type: 'set_swap_group', payload: {
                              chain: sw.chain, signatures: [sw.signature], groupNo: sw.groupNo, role: r } }), g)}
                            <span className={`flex-1 min-w-0 truncate ${muted}`}>
                              {fmtSigned(-sw.inAmount)} {sw.inSymbol} · {fmtSigned(sw.outAmount)} {sw.outSymbol}
                            </span>
                            {sp && (
                              <span className="shrink-0 tabular-nums flex items-center gap-1"
                                title={`1 ${sp.base} = ${fmtPrice(sp.price)} ${sp.quote}`}>
                                <Scale size={10} className={muted} />
                                {fmtPrice(sp.price)} <span className={muted}>{sp.quote}/{sp.base}</span>
                              </span>
                            )}
                            <span className={`ml-3 shrink-0 text-[10px] ${muted}`}>{fmtDate(sw.ts)}</span>
                          </div>
                        );
                      })}
                      {/* Perp işlemleri — LP satırlarının altında, taraf bazında */}
                      {leg?.sides && (['LONG', 'SHORT'] as const).map(sd => {
                        const sv = leg.sides![sd];
                        if (!sv || !sv.qty) return null;
                        return (
                          <div key={sd} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${sub} border`}>
                            <span className={`font-mono tabular-nums w-12 shrink-0 ${muted}`}>perp</span>
                            <span className="font-medium shrink-0">{leg.symbol}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${sd === 'LONG'
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                              : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'}`}>{sd.toLowerCase()}</span>
                            <span className={`flex-1 min-w-0 truncate ${muted}`}>
                              {fmt(sv.qty)} · {sv.count} işlem · fee {fmt(sv.fee, 6)}
                            </span>
                            <span className="shrink-0 tabular-nums flex items-center gap-1">
                              <Scale size={10} className={muted} />
                              {fmtPrice(sv.avgPrice)} <span className={muted}>USD/{leg.symbol.split('-')[0]}</span>
                            </span>
                            <span className={`ml-3 shrink-0 text-[10px] ${muted}`}>{leg.botId}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sağ: kompakt sonuç kartı */}
                  <div className={`shrink-0 w-56 rounded-lg border ${sub} p-2.5 text-[11px] space-y-2`}>
                    {/* 1) Delta PnL (veya ters yönlüyse ort. fiyat) */}
                    <div>
                      <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}>
                        {g.kind === 'price' ? <><Scale size={10} /> Ort. fiyat</> : <><TrendingUp size={10} /> Delta {g.kind === 'loss' ? 'zarar' : 'PnL'}</>}
                      </div>
                      {g.kind === 'price' && (
                        <>
                          <div className="font-semibold tabular-nums">{g.hasOpen ? '≈ ' : ''}{g.quote === 'USD' ? `$${fmtPrice(g.price!)}` : `${fmtPrice(g.price!)} ${g.quote}`}</div>
                          <div className={`${muted} text-[10px]`}>1 {g.base} başına</div>
                          {g.priceFrom != null && g.priceFrom < g.members.length && (
                            <div className="text-[10px] text-amber-400/80 mb-1"
                              title="Çift taraflı deposit yapan üyeler takas fiyatı üretmez; ortalama yalnız takas yapan bacaklardan kurulur.">
                              {g.priceFrom}/{g.members.length} üyeden
                            </div>
                          )}
                          {(g.priceFrom == null || g.priceFrom >= g.members.length) && <div className="mb-1" />}
                          <div className={`${muted} text-[10px]`}>Net delta</div>
                        </>
                      )}
                      {/* Net delta token satırları — hem fiyat hem kâr/zarar durumunda gösterilir */}
                      <div className="space-y-0.5">
                        {g.delta.map(t => (
                          <div key={t.sym} className={`tabular-nums font-medium ${t.amount >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtSigned(t.amount)} {t.sym}
                          </div>
                        ))}
                      </div>
                      <div className={`${muted} text-[10px] tabular-nums`}>{fmtUsd(g.deltaUsd)}</div>
                    </div>

                    {/* 1c) DELTA / HEDEF ayrımı — yalnız satırlar elle etiketlendiyse.
                        Hiç etiket yoksa `split` null kalır ve kart birebir eski
                        görünümünü korur (geçmişe hiçbir şey yayılmadı, kullanıcı kararı). */}
                    {g.split && (() => {
                      const sp = g.split!;
                      const cizgi = dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]';
                      const sapan = sp.recon.filter(r => Math.abs(r.fark) > 1e-6 * Math.max(1, Math.abs(r.kart)));
                      return (
                        <>
                          {sp.deltaCount > 0 && (
                            <div className={`pt-2 border-t ${cizgi}`}>
                              <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}
                                title="Çalışma sermayesinin havuzda döndüğü bacaklar: sat, geri al, her turda sermaye token'ı olarak daha fazlasıyla kapan. Kazanç BURADA doğar — hedef bloğu kâr üretmez, yalnız bankalar.">
                                <TrendingUp size={10} /> Delta · {sp.kapaliTur} tur
                                {sp.turlar.length > sp.kapaliTur && (
                                  <span className="text-amber-400/80">+1 açık</span>
                                )}
                              </div>
                              <div className="space-y-0.5">
                                {sp.deltaNet.length === 0 && <div className={muted}>—</div>}
                                {sp.deltaNet.map(t => (
                                  <div key={t.sym} className={`tabular-nums ${t.amount >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    {fmtSigned(t.amount)} {t.sym}
                                  </div>
                                ))}
                              </div>
                              {sp.avgSatis != null && sp.avgAlis != null && (
                                <div className={`${muted} text-[10px] tabular-nums`}
                                  title={`Satış = "aç" bacaklarının ağırlıklı ortalaması, alış = "kapa" bacaklarının. ${sp.quoteSym}/${sp.capToken}`}>
                                  sat {fmtPrice(sp.avgSatis)} · al {fmtPrice(sp.avgAlis)}
                                </div>
                              )}
                              {sp.makas != null && (
                                <div className={`${muted} text-[10px] tabular-nums`}
                                  title="Makas = ort. satış / ort. alış − 1. Turun ham getirisi; gerçekleşen kazanç karşı tokende kalan artık yüzünden bundan düşük olabilir.">
                                  makas %{(sp.makas * 100).toFixed(2)}
                                  {sp.ortSure > 0 && ` · tur ~${fmtSure(sp.ortSure)}`}
                                </div>
                              )}
                              {/* Sermaye + getiri. Sermaye otomatik: İLK turun "aç" tarafı. */}
                              <div className={`mt-1 pt-1 border-t border-dashed ${cizgi}`}>
                                {capEdit === g.no ? (
                                  <div className="flex items-center gap-1">
                                    <input autoFocus value={capAmtIn} onChange={e => setCapAmtIn(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') saveCap(g.no); if (e.key === 'Escape') setCapEdit(0); }}
                                      placeholder="miktar"
                                      className={`w-14 text-[10px] bg-transparent border-b border-sky-500/60 outline-none ${text}`} />
                                    <input value={capTokIn} onChange={e => setCapTokIn(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') saveCap(g.no); if (e.key === 'Escape') setCapEdit(0); }}
                                      placeholder="token"
                                      className={`w-12 text-[10px] bg-transparent border-b border-sky-500/60 outline-none ${text}`} />
                                    <button onClick={() => saveCap(g.no)} className="text-[10px] text-sky-400">ok</button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className={`${muted} text-[10px]`}>sermaye</span>
                                    <button
                                      onClick={() => { setCapEdit(g.no); setCapAmtIn(sp.capAmount ? String(sp.capAmount) : ''); setCapTokIn(sp.capToken); }}
                                      title={groupMeta(g.no)?.capSrc === 'manual'
                                        ? 'Elle girildi. Değiştirmek için tıkla; alanı boşaltıp kaydedersen otomatiğe döner.'
                                        : 'İlk "aç" bacağından okundu (kademeli girişte toplamı). Elle yazmak için tıkla.'}
                                      className="text-[10px] tabular-nums hover:text-sky-400 flex items-center gap-0.5">
                                      {sp.capAmount > 0 ? `${fmt(sp.capAmount)} ${sp.capToken}` : 'belirsiz'}
                                      <Pencil size={8} className={muted} />
                                    </button>
                                    {sp.capZap && (
                                      <span className="text-[10px] text-amber-400" title="Bu satır bir ZAP: kaydedilen miktar havuza gireni değil CÜZDAN BAKİYE DEĞİŞİMİNİ gösterir, sermaye token'ı satırda hiç belirmeyebilir. Sayıyı kontrol et, gerekirse elle yaz.">zap?</span>
                                    )}
                                    {sp.getiri != null && sp.capAmount > 0 && (
                                      <span className={`text-[10px] ml-auto tabular-nums font-semibold ${sp.getiri >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                                        title={`Üretilen ${fmt(sp.uretilenCap)} ${sp.capToken} ÷ sermaye ${fmt(sp.capAmount)} ${sp.capToken}. Token cinsinden hesaplanır → fiyat hareketinden bağımsızdır.`}>
                                        %{(sp.getiri * 100).toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {sp.hedefCount > 0 && (
                            <div className={`pt-2 border-t ${cizgi}`}>
                              <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}
                                title="Biriken kazancın hedef token'a çevrildiği bacaklar. Burada kâr/zarar DOĞMAZ; bu blok yalnız kazancın hangi fiyattan bankalandığını gösterir.">
                                <Target size={10} /> Hedef → {sp.hedefToken || '?'}
                              </div>
                              {sp.hedefIn.map(t => (
                                <div key={t.sym} className="tabular-nums text-rose-400">
                                  {fmtSigned(t.amount)} {t.sym}
                                </div>
                              ))}
                              {sp.hedefOut > 0 && (
                                <div className="tabular-nums text-emerald-400">+{fmt(sp.hedefOut)} {sp.hedefToken}</div>
                              )}
                              {sp.hedefPrice != null && (
                                <div className={`${muted} text-[10px] tabular-nums`}
                                  title={`Gerçekleşme fiyatı: ${fmt(sp.hedefOut)} ${sp.hedefToken} ÷ ${fmt(Math.abs(sp.girenKapali[0].amount))} ${sp.girenKapali[0].sym} (yalnız KAPANMIŞ tahsil bacakları; havuzda bekleyen girdi henüz bir şey üretmedi)`}>
                                  @ {fmtPrice(sp.hedefPrice)}
                                  {sp.avgSatis != null && sp.hedefToken === sp.quoteSym
                                    && sp.girenKapali.length === 1 && sp.girenKapali[0].sym === sp.capToken && (
                                    <span className={sp.hedefPrice >= sp.avgSatis ? ' text-emerald-400' : ' text-amber-400'}
                                      title="Kazancı, delta turlarındaki ortalama satış fiyatına göre nereden bankaladın.">
                                      {' '}· satışın %{(Math.abs(sp.hedefPrice / sp.avgSatis - 1) * 100).toFixed(1)}{' '}
                                      {sp.hedefPrice >= sp.avgSatis ? 'üstü' : 'altı'}
                                    </span>
                                  )}
                                </div>
                              )}
                              {sp.hedefPool.length > 0 && (
                                <div className={`${muted} text-[10px] tabular-nums`}
                                  title="Tahsil pozisyonu hâlâ açık: kazanç yolda, henüz hedef token'a dönmedi.">
                                  yolda {sp.hedefPool.map(t => `${fmt(t.amount)} ${t.sym}`).join(' · ')}
                                </div>
                              )}
                              {tgtEdit === g.no ? (
                                <div className="flex items-center gap-1 mt-1">
                                  <input autoFocus value={tgtAmtIn} onChange={e => setTgtAmtIn(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveTgt(g.no); if (e.key === 'Escape') setTgtEdit(0); }}
                                    placeholder="hedef"
                                    className={`w-14 text-[10px] bg-transparent border-b border-sky-500/60 outline-none ${text}`} />
                                  <input value={tgtTokIn} onChange={e => setTgtTokIn(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveTgt(g.no); if (e.key === 'Escape') setTgtEdit(0); }}
                                    placeholder="token"
                                    className={`w-12 text-[10px] bg-transparent border-b border-sky-500/60 outline-none ${text}`} />
                                  <button onClick={() => saveTgt(g.no)} className="text-[10px] text-sky-400">ok</button>
                                </div>
                              ) : sp.targetAmount > 0 ? (
                                <div className="mt-1">
                                  <div className={`h-1 rounded border ${sub} overflow-hidden`}>
                                    <div className="h-full bg-emerald-500/60"
                                      style={{ width: `${Math.min(100, (sp.hedefProgress || 0) * 100)}%` }} />
                                  </div>
                                  <button onClick={() => { setTgtEdit(g.no); setTgtAmtIn(String(sp.targetAmount)); setTgtTokIn(sp.hedefToken); }}
                                    className={`${muted} text-[10px] tabular-nums hover:text-sky-400`}>
                                    {fmt(sp.hedefOut)} / {fmt(sp.targetAmount)} {sp.hedefToken} · %{((sp.hedefProgress || 0) * 100).toFixed(1)}
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => { setTgtEdit(g.no); setTgtAmtIn(''); setTgtTokIn(sp.hedefToken); }}
                                  title="Bu gruptan toplamayı hedeflediğin miktar — ilerleme çubuğu bundan hesaplanır."
                                  className={`${muted} text-[10px] hover:text-sky-400`}>+ hedef miktar</button>
                              )}
                            </div>
                          )}

                          {/* Mutabakat — token BAZINDA, fiyat varsayımı yok.
                              Kapanmazsa bir satır yanlış etiketlenmiştir. */}
                          <div className={`pt-1.5 mt-1.5 border-t border-dashed ${cizgi}`}>
                            {sp.reconOk ? (
                              <div className="text-[10px] text-emerald-400 flex items-center gap-1"
                                title="Her token için: (kapalı turların neti + fee/reward − masraf) + hedef neti = kartın Toplam'ı (havuzdaki açık bakiye ve tur ortasındaki sermaye hariç). Fiyat içermez, bu yüzden bozulamaz.">
                                <CheckIcon size={10} /> mutabakat
                              </div>
                            ) : (
                              <div className="text-[10px] text-amber-400"
                                title="Bloklardan çıkan sonuç kartın Toplam'ıyla tutmuyor: muhtemelen bir satır atanmamış ya da yanlış atanmış.">
                                ⚠ fark {sapan.slice(0, 2).map(r => `${fmtSigned(r.fark)} ${r.sym}`).join(' · ')}
                              </div>
                            )}
                            {sp.untagged > 0 && (
                              <div className={`${muted} text-[10px]`}
                                title="Etiketsiz satırlar hiçbir bloğa girmez — yalnız genel Toplam'da görünürler.">
                                {sp.untagged} satır atanmamış
                              </div>
                            )}
                            {sp.midTur.length > 0 && (
                              <div className={`${sp.midSuphe ? 'text-amber-400' : muted} text-[10px] tabular-nums`}
                                title={sp.midSuphe
                                  ? 'Eşleşmeyen bacak var: ya açık tur sermayenin yanında çok küçük (yanlışlıkla Δ işaretlenmiş bir TAHSİL bacağı olabilir, $ ile değiştir) ya da birden fazla tur kapanmamış durumda.'
                                  : 'Açık tur: sermaye yolun ortasında, henüz geri alınmadı. Kazanç/zarar değil → mutabakata girmez.'}>
                                {sp.midSuphe ? '⚠ ' : ''}tur ortası {sp.midTur.map(t => `${fmtSigned(t.amount)} ${t.sym}`).join(' · ')}
                              </div>
                            )}
                            {sp.yabanci.length > 0 && (
                              <div className="text-[10px] text-amber-400/80"
                                title="Tek sermaye token'ı disiplini: delta bacaklarında üçüncü bir token belirdi, grup çorbalaşmaya başlıyor.">
                                çorba: {sp.yabanci.join(', ')}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}

                    {/* 1b) Havuzda duran açık sermaye — delta'da eksi görünür ama kaybedilmiş değil */}
                    {g.pool.length > 0 && (
                      <div className={`pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                        <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}
                          title="Açık üyelerin havuzda duran anlık bakiyesi. Net delta'da yatırılan sermaye eksi görünüyor; burada geri sayılıyor.">
                          <Wallet size={10} /> Havuzda (açık)
                        </div>
                        <div className="space-y-0.5">
                          {g.pool.map(t => (
                            <div key={t.sym} className="tabular-nums text-sky-300">
                              {fmtSigned(t.amount)} {t.sym}
                            </div>
                          ))}
                        </div>
                        <div className={`${muted} text-[10px] tabular-nums`}>{fmtUsd(g.poolUsd)}</div>
                      </div>
                    )}

                    {/* 2) Fee + Reward */}
                    <div className={`pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                      <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}><Receipt size={10} /> Fee + Reward</div>
                      <div className="space-y-0.5">
                        {g.feeReward.length === 0 && <div className={muted}>—</div>}
                        {g.feeReward.map(t => (
                          <div key={t.sym} className="tabular-nums">{fmt(t.amount)} <span className={muted}>{t.sym}</span></div>
                        ))}
                      </div>
                      <div className={`${muted} text-[10px] tabular-nums`}>{fmtUsd(g.feeRewardUsd)}</div>
                    </div>

                    {/* 2b) Masraf (Arc: gaz + launch ücreti) — Toplam'dan düşülür */}
                    {g.cost.length > 0 && (
                      <div className={`pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                        <div className={`${muted} text-[10px] mb-0.5`}>Masraf (gaz/launch)</div>
                        {g.cost.map(t => (
                          <div key={t.sym} className="tabular-nums text-orange-400">−{fmt(t.amount, 6)} <span className={muted}>{t.sym}</span></div>
                        ))}
                        <div className={`${muted} text-[10px] tabular-nums`}>−{fmtUsd(g.costUsd)}</div>
                      </div>
                    )}

                    {/* 2c) Kilitli kira — Solana'da pozisyon hesaplarında bağlı duran
                        sermaye. Kapanışta tamamı geri döndüğü için Toplam'dan
                        DÜŞÜLMEZ; masrafla aynı satıra koymak kaybedilmiş gösterirdi. */}
                    {g.rent.length > 0 && (
                      <div className={`pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                        <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}
                          title="Solana'da pozisyon açmak NFT mint + NFT hesabı + pozisyon hesabında kira kilitler (toplam 0,0066802 SOL). Kapanışta tamamı cüzdana döner → masraf değil, geçici bağlanmış sermaye. Bu yüzden Toplam'a girmez.">
                          <Wallet size={10} /> Kira (kilitli, iade edilir)
                        </div>
                        {g.rent.map(t => (
                          <div key={t.sym} className="tabular-nums text-amber-400/90">{fmt(t.amount, 7)} <span className={muted}>{t.sym}</span></div>
                        ))}
                        <div className={`${muted} text-[10px] tabular-nums`}>{fmtUsd(g.rentUsd)}</div>
                      </div>
                    )}

                    {/* 3) Toplam (token + USD) */}
                    <div className={`pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                      <div className={`${muted} text-[10px] mb-0.5`}
                        title={g.pool.length > 0
                          ? 'Net delta + havuzdaki açık sermaye + fee/reward'
                          : 'Net delta + fee/reward'}>
                        Toplam{arb ? ' (spot + perp)' : g.pool.length > 0 ? ' (havuz dahil)' : ''}
                      </div>
                      <div className="space-y-0.5">
                        {g.total.map(t => (
                          <div key={t.sym} className={`tabular-nums ${t.amount >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtSigned(t.amount)} {t.sym}
                          </div>
                        ))}
                      </div>
                      {arb ? (
                        <>
                          <div className={`${muted} text-[10px] tabular-nums mt-1`}>spot {fmtUsd(g.totalUsd)}</div>
                          <div className={`${muted} text-[10px] tabular-nums`}>funding {fmtUsd(leg!.fundingUsd)}</div>
                          <div className={`${muted} text-[10px] tabular-nums`}>perp PnL {fmtUsd(arb.rPnl + arb.uPnl)}</div>
                          <div className={`${muted} text-[10px] tabular-nums`}>komisyon −{fmtUsd(leg!.fee).slice(1)}</div>
                          <div className={`text-base font-bold tabular-nums mt-0.5 ${arb.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtUsd(arb.net)}
                          </div>
                        </>
                      ) : (
                        <div className={`font-semibold tabular-nums ${g.totalUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {fmtUsd(g.totalUsd)}
                        </div>
                      )}

                      {/* Fee+reward dahil ortalama fiyat — yalnız KAPALI üyelerden.
                          Üstteki "Ort. fiyat" net delta'dan (fee hariç) kuruluyor;
                          ikisi aynı yönde gösterildiği için doğrudan karşılaştırılır. */}
                      {g.totalPrice != null && (
                        <div className={`mt-1.5 pt-1.5 border-t border-dashed ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                          <div className={`${muted} text-[10px] mb-0.5 flex items-center gap-1`}
                            title="Toplam'ın en büyük artı ve en büyük eksi kalemi takas çifti sayılır (büyüklük USD'ye göre). Fee, reward ve masraf dahil. AÇIK üyeler hariç: havuzdaki sermayeleri ve claim edilmemiş fee'leri her turda değişir, fiyatı oynatırdı.">
                            <Scale size={10} /> Ort. fiyat (fee dahil)
                          </div>
                          <div className="font-semibold tabular-nums">
                            {fmtPrice(g.totalPrice)} <span className={muted}>{g.totalQuote}</span>
                          </div>
                          <div className={`${muted} text-[10px]`}>
                            1 {g.totalBase} başına
                            {g.closedCount < g.members.length && ` · ${g.closedCount}/${g.members.length} kapalı üyeden`}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Funding arbitrage: perp bacağı (işaretli trade'lerden) ── */}
                {(() => {
                  if (!leg || !arb) return null;
                  const a = arb;
                  const L = leg.live;
                  return (
                    <div className={`mt-3 pt-3 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                      <div className={`flex items-center gap-2 mb-2 text-[11px] ${muted}`}>
                        <Link2 size={12} className="text-sky-400" />
                        <span className="font-medium">Funding arbitrage</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${sub} border`}>{leg.symbol}</span>
                        <span>{leg.tradeCount} işlem · {leg.botId}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px]">
                        <div>
                          <div className={`${muted} text-[10px]`}>Perp pozisyon</div>
                          <div className={`font-medium tabular-nums ${leg.netQty >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtSigned(leg.netQty)} {a.base}
                          </div>
                          <div className={`${muted} text-[10px] tabular-nums`}>giriş {fmtPrice(leg.avgEntry)}
                            {L ? ` · mark ${fmtPrice(L.markPrice)}` : ''}</div>
                        </div>
                        <div>
                          <div className={`${muted} text-[10px]`}>Hedge açığı</div>
                          <div className={`font-medium tabular-nums ${Math.abs(a.hedgeGap) < 1e-6 ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {Math.abs(a.hedgeGap) < 1e-6 ? 'tam hedge' : `${fmtSigned(a.hedgeGap)} ${a.base}`}
                          </div>
                          <div className={`${muted} text-[10px] tabular-nums`}>LP {fmtSigned(a.lpQty)}</div>
                        </div>
                        <div>
                          <div className={`${muted} text-[10px]`}>Basis</div>
                          <div className={`font-medium tabular-nums ${a.basisPct == null ? muted : a.basisPct <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {a.basisPct == null ? '—' : `${a.basisPct >= 0 ? '+' : ''}${a.basisPct.toFixed(2)}%`}
                          </div>
                          <div className={`${muted} text-[10px] tabular-nums`}>
                            {a.lpPrice ? `LP ${fmtPrice(a.lpPrice)}` : 'kıyas yok'}
                          </div>
                        </div>
                        <div>
                          <div className={`${muted} text-[10px]`}>Funding</div>
                          <div className={`font-medium tabular-nums ${leg.fundingUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtUsd(leg.fundingUsd)}
                          </div>
                          <div className={`${muted} text-[10px] tabular-nums`}>
                            perp PnL {fmtUsd(a.rPnl + a.uPnl)}
                          </div>
                        </div>
                        <div>
                          <div className={`${muted} text-[10px]`}>Net (spot+perp)</div>
                          <div className={`text-base font-semibold tabular-nums ${a.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtUsd(a.net)}
                          </div>
                          <div className={`${muted} text-[10px] tabular-nums`}>komisyon −{fmtUsd(leg.fee).slice(1)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              );
  };

  // Dropdown dışına tıklayınca kapat
  useEffect(() => {
    if (!tokenMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tokenMenuRef.current && !tokenMenuRef.current.contains(e.target as Node)) setTokenMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tokenMenuOpen]);

  // ── Tema ────────────────────────────────────────────────────────────────────
  const bg      = dark ? 'bg-[#03112b]' : 'bg-[#f4f7fb]';
  const card    = dark ? 'bg-[#061d3f] border-[#0a2560]' : 'bg-white border-[#d4e0f0]';
  const sub     = dark ? 'bg-[#03112b]/60 border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const text    = dark ? 'text-blue-50' : 'text-slate-800';
  const muted   = dark ? 'text-blue-300/70' : 'text-slate-500';
  const pop     = dark ? 'bg-[#0a2560] border-[#164a9e] text-blue-50' : 'bg-white border-[#c3d3ea] text-slate-800';
  const inputCls = dark
    ? 'bg-[#03112b] border-[#0a2560] text-blue-50 placeholder-blue-300/40'
    : 'bg-white border-[#d4e0f0] text-slate-800 placeholder-slate-400';

  // Soldaki ray için yaşayan grup listesi. Kaynak HAFİF meta listesi (`allGroups`):
  // ad, sıra ve durum orada zaten var → grup pozisyonlarını yüklemeye gerek yok,
  // sayfa açılışı yavaşlamaz. Ad verilmiş/verilmemiş ayrımı YOK: adsız gruplar da
  // listelenir, etiket `groupLabel` ile "Grup N"e düşer.
  const livingGroups = useMemo(
    () => allGroups
      .filter((g: any) => (!g.wallet || g.wallet === wallet) && g.status !== 'closed')
      .sort((a: any, b: any) => b.no - a.no),
    [allGroups, wallet]);

  // Sağ raydan bota geçiş. LP Trax Router'ın DIŞINDA render edildiği için (App.tsx
  // standalone dalı) bot state'ini paylaşamıyoruz: id sessionStorage'a bırakılıp
  // `/`'ye tam sayfa yönlendirme yapılıyor, App orada devri alıp bot seçim
  // ekranını atlıyor. Anahtarı App siliyor — burada silmek yarışa yol açar.
  const botaGec = (id: string) => {
    try { sessionStorage.setItem(PENDING_BOT_KEY, id); } catch { /* özel sekme */ }
    window.location.assign('/');
  };

  return (
    <div className={`min-h-full ${bg} ${text} p-5`}>
      {/* ── Sol ray: yaşayan gruplar ───────────────────────────────────────────
          Sayfa `max-w-5xl` ile ortalandığı için geniş ekranda iki yanda ölü alan
          kalıyor; ray o boşluğa oturur. 1440px ALTINDA BASILMAZ: 1280px'de boşluk
          (1280−1024)/2 = 128px, ray 176px → içeriğin üstüne binerdi. Dar ekranda
          sayfa eskisi gibi kalır, gruplara pozisyon rozetinden erişilir. */}
      {tab === 'open' && !filteredIds && livingGroups.length > 0 && (
        <aside className="hidden min-[1440px]:block fixed left-4 top-20 w-44 z-20">
          <div className={`rounded-xl border ${card} p-2 shadow-lg`}>
            <div className={`${muted} text-[10px] uppercase tracking-wide px-1 pb-1.5 flex items-center gap-1`}>
              <Layers size={10} /> Yaşayan gruplar ({livingGroups.length})
            </div>
            <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-0.5">
              {livingGroups.map((g: any) => (
                <button key={g.no} onClick={() => openGroupModal(g.no, g.rank || g.no)}
                  title={groupTitle(g, g.rank) || `Grup ${g.rank || g.no} · işlemler ve delta/fiyat hesabı için tıkla`}
                  className={`w-full text-left truncate px-2 py-1 rounded-md border text-[11px] transition ${
                    groupModal?.no === g.no
                      ? 'bg-sky-500/15 text-sky-400 border-sky-500/40 font-medium'
                      : `${sub} ${muted} hover:text-sky-400 hover:border-sky-500/40`}`}>
                  {groupLabel(g, g.rank || g.no)}
                </button>
              ))}
            </div>
          </div>
        </aside>
      )}

      {/* ── Sağ ray: aktif botlar ──────────────────────────────────────────────
          Soldaki rayın aynadaki eşi; sağdaki ölü alana oturur. Aynı 1440px
          eşiği: 1280px'de sağ boşluk da 128px, ray 176px → içeriğe binerdi.
          Liste `activeBots.ts`te, üst şeritle TEK kaynağı paylaşıyor. */}
      <aside className="hidden min-[1440px]:block fixed right-4 top-20 w-44 z-20">
        <div className={`rounded-xl border ${card} p-2 shadow-lg`}>
          <div className={`${muted} text-[10px] uppercase tracking-wide px-1 pb-1.5 flex items-center gap-1`}>
            <Bot size={10} /> Aktif botlar
          </div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-0.5">
            {ACTIVE_BOTS.map(b => (
              <button key={b.id} onClick={() => botaGec(b.id)}
                title={`${b.name} paneline geç`}
                className={`w-full text-left truncate px-2 py-1 rounded-md border text-[11px] transition ${sub} ${muted} hover:text-sky-400 hover:border-sky-500/40`}>
                {b.name}
              </button>
            ))}
          </div>
          <button onClick={() => window.location.assign('/')}
            title="Tüm botların bulunduğu seçim ekranı (Diğerleri dahil)"
            className={`mt-1.5 w-full flex items-center justify-center gap-1 px-2 py-1 rounded-md border text-[10px] uppercase tracking-wide transition ${sub} ${muted} hover:text-sky-400 hover:border-sky-500/40`}>
            <Layers size={10} /> Bot seçimi
          </button>
        </div>
      </aside>

      <div className="max-w-5xl mx-auto">
        {/* Başlık + cüzdan seçici */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => window.location.assign('/')} title="Bot seçimine dön"
              className={`p-2 rounded-lg border ${card} hover:opacity-80`}>
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2"><Wallet size={18} /> LP Tracker</h1>
              <p className={`text-xs ${muted}`}>Sui CLMM · Cetus · Momentum · Bluefin · Turbos · Full Sail · Solana Raydium · Arc Uniswap v4</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 text-xs ${muted}`}>
              <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-rose-500'}`} />
              {connected ? 'bağlı' : 'bağlanıyor'}
            </div>
            {isSuiAddr(wallet) && (
              <button onClick={() => setShowAuth(v => !v)} title="Bluefin Pro okuyucu yetkisi (tek seferlik)"
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${card} hover:opacity-80 ${
                  showAuth ? 'text-amber-400 border-amber-500/40' : 'hover:text-amber-400'}`}>
                <KeyRound size={13} /> Bluefin yetki
              </button>
            )}
            <button onClick={() => window.location.assign('/navi')} title="Navi lending/borrowing takibi"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${card} hover:opacity-80 hover:text-sky-400`}>
              <Landmark size={13} /> Navi
            </button>
            <button onClick={() => window.location.assign('/suilend')} title="Suilend (Bluefin Lend) lending/borrowing takibi"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${card} hover:opacity-80 hover:text-sky-400`}>
              <Banknote size={13} /> Suilend
            </button>
            {/* Bütçe ayrı kök (/butce): LP/lending aileleriyle veri paylaşmıyor,
                sadece aynı uygulamadan erişilsin diye şeritte duruyor. */}
            <button onClick={() => window.location.assign('/butce')} title="Bütçe — kişisel gelir/gider defteri"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${card} hover:opacity-80 hover:text-violet-300`}>
              <CalendarDays size={13} /> Bütçe
            </button>
            <button onClick={() => forceSync(wallet)} disabled={!wallet || loading}
              title="Zincirden çek + aç/kapa durumunu güncelle"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${card} hover:opacity-80 disabled:opacity-40`}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> {loading ? 'Güncelleniyor…' : 'Yenile'}
            </button>
          </div>
        </div>

        {/* Cüzdan satırı */}
        <div className={`rounded-xl border ${card} p-3 mb-4`}>
          <div className="flex items-center gap-2 flex-wrap">
            {wallets.map(w => {
              const nm = walletNames[w] || '';
              const ch = walletChain(w);
              const active = w === wallet;
              if (editWallet === w) {
                return (
                  <span key={w} className={`text-xs px-2 py-1 rounded-lg border ${sub} flex items-center gap-1`}>
                    <input autoFocus value={editName} maxLength={40}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { saveWalletName(w); }
                        if (e.key === 'Escape') { setEditWallet(''); }
                      }}
                      onBlur={() => saveWalletName(w)}
                      placeholder={shortId(w)}
                      className={`text-xs px-1.5 py-0.5 rounded border w-36 ${inputCls} focus:outline-none focus:border-sky-500/60`} />
                    <button onClick={() => saveWalletName(w)} title="Kaydet (Enter) — boş bırakırsan adrese döner"
                      className="text-emerald-400"><CheckIcon size={12} /></button>
                  </span>
                );
              }
              return (
                <span key={w}
                  className={`text-xs pl-2.5 pr-1.5 py-1 rounded-lg border transition-colors flex items-center gap-1.5 ${
                    active ? 'border-sky-500/60 bg-sky-500/15 text-sky-300' : `${sub} ${muted} hover:opacity-80`}`}>
                  <span className={`text-[9px] px-1 py-0.5 rounded border ${WALLET_BADGE[ch]}`}>{CHAIN_LABEL[ch]}</span>
                  {/* İsim adresi GİZLEMEZ: adres hep yanında ve kopyalanabilir —
                      yanlış cüzdana işlem yapmanın bedeli yüksek. */}
                  <button onClick={() => selectWallet(w)}
                    onDoubleClick={() => { setEditWallet(w); setEditName(nm); }}
                    title={nm ? `${nm} · ${w}` : w} className="flex items-center gap-1.5">
                    <span>{nm || shortId(w)}</span>
                    {nm && <span className="opacity-50 font-mono text-[10px]">{shortId(w)}</span>}
                  </button>
                  {active && (
                    <>
                      <button onClick={() => { setEditWallet(w); setEditName(nm); }} title="İsmi değiştir"
                        className="opacity-60 hover:opacity-100"><Pencil size={11} /></button>
                      <button onClick={() => copyWallet(w)} title="Adresi kopyala"
                        className="opacity-60 hover:opacity-100">
                        {copied === w ? <CheckIcon size={11} className="text-emerald-400" /> : <Copy size={11} />}
                      </button>
                    </>
                  )}
                </span>
              );
            })}
            <div className="flex items-center gap-1.5 ml-auto">
              <div className="relative">
                <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
                <input value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runLookup()}
                  placeholder="cüzdan / position id / tx id"
                  className={`text-xs pl-8 pr-3 py-1.5 rounded-lg border w-64 ${inputCls} focus:outline-none focus:border-sky-500/60`} />
              </div>
              <button onClick={runLookup} disabled={syncing}
                className="text-xs px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 flex items-center gap-1.5">
                {syncing ? <RefreshCw size={13} className="animate-spin" /> : <Search size={13} />} {syncing ? 'Aranıyor…' : 'Ara'}
              </button>
            </div>
          </div>
          {err && <p className="text-xs text-rose-400 mt-2">{err}</p>}
          {banner && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="px-2 py-1 rounded-md bg-sky-500/15 text-sky-300 border border-sky-500/30">{banner}</span>
              <button onClick={clearLookup} className={`${muted} hover:text-sky-400`}>temizle ✕</button>
            </div>
          )}
        </div>

        {showAuth && (() => {
          const addr = (authAddr || (isSuiAddr(wallet) ? wallet : '')).trim();
          const valid = /^0x[0-9a-fA-F]{40,}$/.test(addr);
          return (
            <div className={`rounded-xl border ${card} p-3 mb-3`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold ${text}`}>Bluefin okuma yetkisi</span>
                <span className={`text-[11px] ${muted}`}>
                  zkLogin cüzdanı, backend'in okuyucu anahtarını yetkilendirir — zincir dışı, gaz yok
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  value={authAddr}
                  onChange={e => setAuthAddr(e.target.value)}
                  placeholder={isSuiAddr(wallet) ? wallet : 'yetkilendirilecek Sui adresi (0x…)'}
                  className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-mono ${inputCls}`} />
                {authAddr && (
                  <button onClick={() => setAuthAddr('')}
                    className={`text-[11px] ${muted} hover:text-rose-400`}>temizle ✕</button>
                )}
              </div>
              {addr && !valid && (
                <p className="text-[11px] text-amber-400">Geçerli bir Sui adresi değil.</p>
              )}
              {valid
                ? <BluefinAuthorize dark={dark} account={addr} />
                : <p className={`text-[11px] ${muted}`}>Adres gir — panel o hesap için açılacak.</p>}
            </div>
          );
        })()}

        {/* Sekmeler — arama aktifken gizlenir: sonuç sekmeden bağımsız gelir,
            yan yana durunca "Açık sekmesindeyim ama kapalı pozisyon görüyorum" oluyordu. */}
        <div className={`flex items-center gap-2 mb-3 text-xs ${filteredIds ? 'hidden' : ''}`}>
          {([['open', `Açık${summary.open ? ` (${summary.open})` : ''}`],
             ['closed', `Kapalı${summary.closed ? ` (${summary.closed})` : ''}`],
             ['groups', `Gruplar${groups.length ? ` (${groups.length})` : ''}`],
             ['swaps', `Swaplar${swapTotal ? ` (${swapTotal})` : ''}`]] as const).map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); loadPositions(walletRef.current, k, 0); }}
              className={`px-3 py-1.5 rounded-lg border transition ${tab === k
                ? 'bg-sky-500/15 text-sky-400 border-sky-500/40 font-medium'
                : `${sub} ${muted} hover:text-sky-400`}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Filtreler */}
        {tab !== 'groups' && tab !== 'swaps' && (
        <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} dark={dark}
            onApply={(f, t) => {
              setDateFrom(f); setDateTo(t);   // yeniden yükleme filtre effect'inde
            }} />
          {/* Zincir filtresi — yalnız cüzdan birden fazla ağda kullanılmışsa görünür
              (tek zincirli cüzdanda "Tümü / Sui" gereksiz gürültü olurdu). */}
          {chains.length > 1 && (
            <>
              <span className={muted}>Zincir:</span>
              {['all', ...chains].map(c => (
                <button key={c} onClick={() => setChainF(c)}
                  className={`px-2.5 py-1 rounded-md border flex items-center gap-1 ${
                    chainF === c ? 'border-sky-500/60 text-sky-300' : `${sub} ${muted}`}`}>
                  {c !== 'all' && WALLET_BADGE[c] && (
                    <span className={`w-1.5 h-1.5 rounded-full border ${WALLET_BADGE[c]}`} />
                  )}
                  {c === 'all' ? 'Tümü' : CHAIN_LABEL[c] || c}
                </button>
              ))}
              <span className={`${muted} opacity-40`}>·</span>
            </>
          )}
          <span className={muted}>Platform:</span>
          {['all', ...platforms].map(p => (
            <button key={p} onClick={() => setPlatformF(p)}
              className={`px-2.5 py-1 rounded-md border ${platformF === p ? 'border-sky-500/60 text-sky-300' : `${sub} ${muted}`}`}>
              {p === 'all' ? 'Tümü' : PLATFORM_LABEL[p] || p}
            </button>
          ))}
          {/* Durum butonları KALDIRILDI: sekmeler (Açık/Kapalı) aynı işi yapıyordu;
              ikisi birlikte çelişip (tab=Kapalı + filtre=Açık) boş liste üretiyordu. */}

          <button onClick={() => setFavOnly(v => !v)}
            className={`ml-3 px-2.5 py-1 rounded-md border flex items-center gap-1 ${
              favOnly ? 'border-amber-400/60 text-amber-400' : `${sub} ${muted}`}`}>
            <Star size={12} className={favOnly ? 'fill-amber-400' : ''} /> Favoriler
          </button>

          {/* Token filtresi — çoklu seçim dropdown */}
          <span className={`${muted} ml-3`}>Token:</span>
          <div className="relative" ref={tokenMenuRef}>
            <button onClick={() => setTokenMenuOpen(o => !o)}
              className={`px-2.5 py-1 rounded-md border flex items-center gap-1 ${
                tokenF.size > 0 ? 'border-sky-500/60 text-sky-300' : `${sub} ${muted}`}`}>
              {tokenF.size === 0 ? 'Tümü' : `${tokenF.size} seçili`}
              <ChevronDown size={12} />
            </button>
            {tokenMenuOpen && (
              <div className={`absolute z-30 mt-1 left-0 w-44 rounded-lg border ${card} shadow-lg p-1 max-h-64 overflow-auto`}>
                {tokenF.size > 0 && (
                  <button onClick={() => setTokenF(new Set())}
                    className={`w-full text-left px-2 py-1 rounded ${muted} hover:text-sky-300`}>
                    Temizle ({tokenF.size})
                  </button>
                )}
                {tokens.length === 0 && <div className={`px-2 py-1 ${muted}`}>token yok</div>}
                {tokens.map(sym => (
                  <label key={sym}
                    className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-sky-500/10 ${
                      tokenF.has(sym) ? 'text-sky-300' : text}`}>
                    <input type="checkbox" checked={tokenF.has(sym)} onChange={() => toggleToken(sym)}
                      className="accent-sky-500" />
                    {sym}
                  </label>
                ))}
              </div>
            )}
          </div>

          <span className={`${muted} ml-auto`}>{filtered.length} pozisyon</span>
        </div>
        )}

        {/* Portföy özeti */}
        {tab !== 'groups' && tab !== 'swaps' && filtered.length > 0 && (
          <div className={`rounded-xl border ${card} p-3 mb-3 grid grid-cols-2 md:grid-cols-5 gap-3`}>
            <div>
              <div className={`text-[11px] ${muted}`}>Pozisyon</div>
              <div className="text-lg font-semibold leading-tight">
                {summary.open} <span className={`text-xs ${muted} font-normal`}>açık</span>
              </div>
              <div className={`text-[11px] ${muted}`}>{summary.closed} kapalı · {summary.total} toplam</div>
            </div>
            <div>
              <div className={`text-[11px] ${muted}`}>Aktif TVL</div>
              <UsdHover tokens={summary.tvlTokens} pop={pop} placement="bottom" align="left">
                <div className="text-lg font-semibold leading-tight">{fmtUsd(summary.tvlUsd)}</div>
              </UsdHover>
              <div className={`text-[11px] ${muted} truncate`}>{summary.plats.map(([p, n]: [string, number]) => `${PLATFORM_LABEL[p] || p} ${n}`).join(' · ')}</div>
            </div>
            <div>
              <div className={`text-[11px] ${muted} flex items-center gap-1`}><Receipt size={11} /> Toplam fee</div>
              <UsdHover tokens={summary.feeTokens} pop={pop} placement="bottom" align="left">
                <div className="text-lg font-semibold leading-tight text-emerald-400">{fmtUsd(summary.feeUsd)}</div>
              </UsdHover>
            </div>
            <div>
              <div className={`text-[11px] ${muted} flex items-center gap-1`}><Gift size={11} /> Toplam reward</div>
              <UsdHover tokens={summary.rewardTokens} pop={pop} placement="bottom" align="left">
                <div className="text-lg font-semibold leading-tight text-violet-400">{fmtUsd(summary.rewardUsd)}</div>
              </UsdHover>
            </div>
            <div>
              <div className={`text-[11px] ${muted} flex items-center gap-1`}><TrendingUp size={11} /> Toplam kazanç</div>
              <UsdHover tokens={summary.earnedTokens} pop={pop} placement="bottom" align="right">
                <div className="text-lg font-semibold leading-tight text-sky-400">{fmtUsd(summary.earnedUsd)}</div>
              </UsdHover>
            </div>
          </div>
        )}

        {/* Pozisyon kartları */}
        {tab !== 'groups' && tab !== 'swaps' && loading && positions.length === 0 && <p className={`text-sm ${muted} py-8 text-center`}>Yükleniyor…</p>}
        {tab !== 'groups' && tab !== 'swaps' && !loading && filtered.length === 0 && <p className={`text-sm ${muted} py-8 text-center`}>Bu cüzdanda pozisyon bulunamadı.</p>}

        <div className={`space-y-2.5 ${tab !== 'groups' && tab !== 'swaps' ? '' : 'hidden'}`}>
          {filtered.slice(0, visibleCount).map(p => {
            const isOpen = expanded.has(p.positionId);
            const evs = eventsMap[p.positionId];
            return (
              <div key={p.positionId} className={`rounded-xl border ${card} overflow-hidden`}>
                {/* Kart başlığı */}
                <button onClick={() => toggle(p.positionId)} className="w-full text-left p-3.5 flex items-center gap-3">
                  {isOpen ? <ChevronDown size={16} className={muted} /> : <ChevronRight size={16} className={muted} />}
                  <div className="flex items-center gap-2 min-w-0">
                    <PairIcons a={p.tokenA} b={p.tokenB} sa={p.symbolA} sb={p.symbolB} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {p.seq > 0 && <span className={`text-[11px] font-mono font-semibold tabular-nums ${muted}`} title="Kronolojik LP numarası">#{p.seq}</span>}
                        <span className="font-semibold text-sm truncate">{p.symbolA} / {p.symbolB}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${muted} ${sub} border`}>{PLATFORM_LABEL[p.platform] || p.platform}</span>
                        {CHAIN_BADGE[p.chain] && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${CHAIN_BADGE[p.chain]}`}>{CHAIN_LABEL[p.chain]}</span>}
                        {(p.platform === 'uniswap_v4' || p.platform === 'uniswap_v3') && p.tickSpacing && p.tickLower != null && p.tickUpper != null
                          && p.tickUpper - p.tickLower === p.tickSpacing && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30"
                            title="Tek tick aralığında pozisyon — fiyat aralığı geçince tamamen diğer tokena döner (LP ile limit emir)">
                            Aralık emri
                          </span>
                        )}
                        {p.platform === 'arc_creator' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            title="Başlattığın token'ın havuzundan hook'un sana ödediği creator ücretleri">
                            Creator ücreti
                          </span>
                        )}
                        {p.incomplete && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/30"
                            title="Açılış işlemi tarama penceresinin dışında — deposit kaydı yok, kâr/fiyat hesaplanamıyor">
                            eksik geçmiş
                          </span>
                        )}
                        {DLMM_PLATFORMS.has(p.platform) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-400 border border-teal-500/30"
                            title="Bin tabanlı DLMM pozisyonu (CLMM'den farklı)">DLMM</span>
                        )}
                        {p.zap && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Zap-in</span>}
                        {p.zapOut && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/30">Zap-out</span>}
                        {p.groupNo > 0 && (
                          /* Rozet = grubun kendi adı + pop-up düğmesi. Kart başlığı bir
                             <button> olduğu için burada iç içe düğme YOK (geçersiz DOM);
                             span + role="button" aynı işi görüyor. */
                          <span role="button" tabIndex={0}
                            onClick={e => { e.stopPropagation(); openGroupModal(p.groupNo, p.groupRank || p.groupNo); }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); e.stopPropagation(); openGroupModal(p.groupNo, p.groupRank || p.groupNo);
                              }
                            }}
                            title={`Grup ${p.groupRank || p.groupNo} · işlemler ve delta/fiyat hesabı için tıkla`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 border border-sky-500/30
                              inline-flex items-center gap-1 cursor-pointer transition hover:bg-sky-500/30 hover:border-sky-500/60">
                            <Layers size={9} />
                            {groupLabel(groupMeta(p.groupNo), p.groupRank || p.groupNo)}{p.role ? ` · ${p.role}` : ''}
                          </span>
                        )}
                      </div>
                      <span className={`text-[11px] font-mono ${muted}`}>{shortId(p.positionId)}</span>
                    </div>
                  </div>

                  {/* Özet metrikler */}
                  <div className="hidden md:flex items-center gap-5 ml-auto text-xs">
                    {p.status === 'open' && p.valueUsd != null && (
                      <UsdHover tokens={[{ sym: p.symbolA, amount: p.curA }, { sym: p.symbolB, amount: p.curB }]} pop={pop} placement="bottom" className="text-right">
                        <div className={`flex items-center justify-end gap-1 ${muted} text-[10px]`}><Wallet size={12} />Değer</div>
                        <div className="font-semibold text-emerald-400">{fmtUsd(p.valueUsd)}</div>
                      </UsdHover>
                    )}
                    <Metric icon={<ArrowDownLeft size={12} />} label={p.zap ? 'Zap-in' : 'Deposit'}
                      a={p.zap ? `${fmtSigned(-p.costA)} ${p.symbolA}` : `${fmt(p.costA)} ${p.symbolA}`}
                      b={p.zap ? `${fmtSigned(-p.costB)} ${p.symbolB}` : `${fmt(p.costB)} ${p.symbolB}`} muted={muted} />
                    <Metric icon={<Receipt size={12} />} label="Fees" a={`${fmt(p.feeA)} ${p.symbolA}`} b={`${fmt(p.feeB)} ${p.symbolB}`} muted={muted} />
                    {p.uncUsd != null && p.uncUsd > 0 && (
                      <UsdHover pop={pop} placement="bottom" className="text-right"
                        tokens={[{ sym: p.symbolA, amount: p.uncFeeA }, { sym: p.symbolB, amount: p.uncFeeB },
                                 ...p.uncRewards.map(r => ({ sym: r.symbol, amount: r.amount }))]}>
                        <div className={`flex items-center justify-end gap-1 ${muted} text-[10px]`}><Sparkles size={12} />Toplanmamış</div>
                        <div className="font-semibold text-amber-400">{fmtUsd(p.uncUsd)}</div>
                      </UsdHover>
                    )}
                    <Metric icon={<Gift size={12} />} label="Rewards" a={p.rewards.length ? `${fmt(p.rewards[0].amount)} ${p.rewards[0].symbol}` : '—'} b={p.rewards.length > 1 ? `+${p.rewards.length - 1} coin` : ''} muted={muted} />
                    {(() => {
                      const m = posMetric(p);
                      if (!m) return null;
                      if (m.kind === 'profit')
                        return <Metric icon={<TrendingUp size={12} />} label="Net kâr"
                          a={`${m.approx ? '≈ ' : ''}+${fmt(m.items[0].amt)} ${m.items[0].sym}`}
                          b={m.items.slice(1).map(i => `+${fmt(i.amt)} ${i.sym}`).join(' · ')} muted={muted} />;
                      return (
                        <>
                          <Metric icon={<Scale size={12} />} label="Ort. fiyat"
                            a={`${m.approx ? '≈ ' : ''}${fmtPrice(m.price)} ${m.quote}`}
                            b={`1 ${m.base} başına`} muted={muted} />
                          {/* Net değişim: sadece deposit+withdraw'dan (fee/reward hariç) */}
                          <div className="text-right">
                            <div className={`flex items-center justify-end gap-1 ${muted} text-[10px]`}>Net değişim</div>
                            <div className={`font-medium ${m.dA >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtSigned(m.dA)} {p.symbolA}</div>
                            <div className={`font-medium ${m.dB >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtSigned(m.dB)} {p.symbolB}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Favori yıldızı + not göstergesi (başlık button'u içinde → stopPropagation) */}
                  <span onClick={(e) => { e.stopPropagation(); setAnnotation(p.positionId, { favorite: !p.favorite }); }}
                    title={p.favorite ? 'Favoriden çıkar' : 'Favorile'}
                    className={`ml-3 shrink-0 cursor-pointer ${p.note ? '' : ''}`}>
                    <Star size={16} className={p.favorite ? 'fill-amber-400 text-amber-400' : `${muted} hover:text-amber-400`} />
                  </span>
                  {p.note && <StickyNote size={13} className={`shrink-0 ${muted}`} aria-label="not var" />}

                  {p.inRange != null && (
                    <span title={`Havuzun anlık tick'i: ${p.curTick} · aralık [${p.tickLower}, ${p.tickUpper}]`}
                      className={`ml-2 shrink-0 text-[10px] px-2 py-1 rounded-full border ${p.inRange === 1
                        ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                        : 'text-amber-400 bg-amber-500/10 border-amber-500/30'}`}>
                      {p.inRange === 1 ? 'aralıkta' : 'aralık dışı'}
                    </span>
                  )}
                  <span className={`ml-2 shrink-0 text-[10px] px-2 py-1 rounded-full flex items-center gap-1 ${
                    p.status === 'open' ? 'text-emerald-400 bg-emerald-500/10' : `${muted} ${sub} border`
                  }`}>
                    <Circle size={7} className="fill-current" /> {p.status === 'open' ? 'açık' : 'kapalı'}
                  </span>
                </button>

                {/* Açılır içerik */}
                {isOpen && (
                  <div className={`border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'} p-3.5`}>
                    {/* Mobil metrikler + tick */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 md:hidden">
                      {p.status === 'open' && p.valueUsd != null && (
                        <MiniStat label="Değer (canlı)"
                          v={`${fmtUsd(p.valueUsd)} · ${fmt(p.curA)} ${p.symbolA} + ${fmt(p.curB)} ${p.symbolB}`} sub={sub} muted={muted} />
                      )}
                      <MiniStat label={p.zap ? 'Zap-in' : 'Deposit'}
                        v={p.zap
                          ? `${fmtSigned(-p.costA)} ${p.symbolA} · ${fmtSigned(-p.costB)} ${p.symbolB}`
                          : `${fmt(p.costA)} ${p.symbolA} + ${fmt(p.costB)} ${p.symbolB}`} sub={sub} muted={muted} />
                      <MiniStat label={p.zapOut ? 'Zap-out' : 'Withdraw'}
                        v={p.zapOut
                          ? `${fmtSigned(p.withdrawA)} ${p.symbolA} · ${fmtSigned(p.withdrawB)} ${p.symbolB}`
                          : `${fmt(p.withdrawA)} ${p.symbolA} + ${fmt(p.withdrawB)} ${p.symbolB}`} sub={sub} muted={muted} />
                      <MiniStat label="Fees" v={`${fmt(p.feeA)} ${p.symbolA} + ${fmt(p.feeB)} ${p.symbolB}`} sub={sub} muted={muted} />
                      <MiniStat label="Rewards" v={p.rewards.map(r => `${fmt(r.amount)} ${r.symbol}`).join(' · ') || '—'} sub={sub} muted={muted} />
                      {(() => {
                        const m = posMetric(p);
                        if (!m) return null;
                        if (m.kind === 'profit')
                          return <MiniStat label="Net kâr"
                            v={`${m.approx ? '≈ ' : ''}${m.items.map(i => `+${fmt(i.amt)} ${i.sym}`).join(' · ')}`}
                            sub={sub} muted={muted} />;
                        return (
                          <>
                            <MiniStat label="Ort. fiyat"
                              v={`${m.approx ? '≈ ' : ''}1 ${m.base} = ${fmtPrice(m.price)} ${m.quote}`}
                              sub={sub} muted={muted} />
                            <MiniStat label="Net değişim"
                              v={`${fmtSigned(m.dA)} ${p.symbolA} · ${fmtSigned(m.dB)} ${p.symbolB}`}
                              sub={sub} muted={muted} />
                          </>
                        );
                      })()}
                    </div>
                    <div className={`flex items-center gap-4 text-[11px] ${muted} mb-3 flex-wrap`}>
                      {(p.chain === 'arc' || p.chain === 'robinhood' || p.platform === 'meteora_dlmm') && (() => {
                        const r = tickRange(p);
                        const unit = p.platform === 'meteora_dlmm' ? 'bin' : 'tick';
                        return r ? <span title={`${unit} [${p.tickLower}, ${p.tickUpper}]`}>
                          Fiyat aralığı: {fmtPrice(r.lo)} – {fmtPrice(r.hi)} {r.quote} / {r.base}</span> : null;
                      })()}
                      {(p.paid || []).length > 0 && (
                        <span className="text-orange-400" title="Ödenen masraf — net kâr ve grup toplamından düşülür">
                          Masraf: {(p.paid || []).map(c => `${KIND_LABEL[c.kind] || c.kind} ${fmt(c.amount, 6)} ${c.symbol}`).join(' · ')}
                        </span>
                      )}
                      <span className={p.chain === 'arc' || p.chain === 'robinhood' || p.platform === 'meteora_dlmm' ? 'hidden' : ''}>Aralık: tick [{p.tickLower ?? '?'}, {p.tickUpper ?? '?'}]
                        {p.inRange != null && (
                          <span className={p.inRange === 1 ? 'text-emerald-400' : 'text-amber-400'}>
                            {' '}· anlık {p.curTick} ({p.inRange === 1 ? 'aralıkta' : 'aralık dışı'})
                          </span>
                        )}
                      </span>
                      <span>Açılış: {fmtDate(p.openedAt)}</span>
                      {p.status === 'closed' && <span>Kapanış: {fmtDate(p.closedAt)}</span>}
                      <span className="hidden md:inline">{p.zapOut ? 'Zap-out' : 'Withdraw'}: {p.zapOut ? `${fmtSigned(p.withdrawA)} ${p.symbolA} · ${fmtSigned(p.withdrawB)} ${p.symbolB}` : `${fmt(p.withdrawA)} ${p.symbolA} + ${fmt(p.withdrawB)} ${p.symbolB}`}</span>
                      <span>Rewards: {p.rewards.map(r => `${fmt(r.amount)} ${r.symbol}`).join(' · ') || '—'}</span>
                      {/* Arc v4: Uniswap arayüzü pozisyonu geç indeksliyor — çekim doğrudan
                          PositionManager'a yazılır. İmza kullanıcıda (bkz. lib/arcExit.ts). */}
                      {p.status === 'open' && p.chain === 'arc' && v4TokenId(p.positionId) != null && (
                        <button onClick={() => setExitPos(p)}
                          className="flex items-center gap-1 text-amber-400 hover:underline ml-auto"
                          title="Uniswap arayüzü olmadan likiditeyi çek">
                          <ArrowDownLeft size={10} /> Likiditeyi çek
                        </button>
                      )}
                      <a href={explorerObj(p.chain, p.positionId)} target="_blank" rel="noreferrer"
                        className={`flex items-center gap-1 text-sky-400 hover:underline ${
                          p.status === 'open' && p.chain === 'arc' && v4TokenId(p.positionId) != null ? '' : 'ml-auto'}`}>
                        {EXPLORER_NAME[p.chain] || 'SuiVision'} <ExternalLink size={10} />
                      </a>
                    </div>

                    {/* Delta grubu */}
                    <div className="mb-3 flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-medium ${muted} flex items-center gap-1`}>
                        <Layers size={12} /> Delta grubu
                      </span>
                      {/* Serbest numara girişi KALDIRILDI: elle numara yazmak iki cüzdanda
                          aynı numarayı üretip grupları çakıştırıyordu. Artık ya mevcut
                          gruplardan seçilir ya da yeni grup açılır (numarayı sunucu verir). */}
                      <select value={p.groupNo || 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'new') sendWs?.({ type: 'create_lp_group', payload: { kind: p.chain || 'sui', wallet: p.wallet, positionId: p.positionId } });
                          else setAnnotation(p.positionId, { groupNo: parseInt(v, 10) || 0 });
                        }}
                        className={`text-xs rounded-lg border px-2 py-1 ${inputCls} focus:outline-none focus:border-sky-500/60`}>
                        <option value={0}>gruba ait değil</option>
                        {/* Kapalı gruplar menüde yok — kapanmış gruba üye eklemek muhasebeyi
                            geriye dönük bozar. İstisna: pozisyonun KENDİ grubu kapalıysa
                            gösterilir, yoksa seçim kutusu yanlışlıkla "gruba ait değil" okur. */}
                        {allGroups.filter((g: any) => (!g.wallet || g.wallet === p.wallet)
                            && (g.status !== 'closed' || g.no === p.groupNo))
                          .sort((a: any, b: any) => a.rank - b.rank)
                          .map((g: any) => (
                            <option key={g.no} value={g.no}>
                              {groupLabel(g, g.rank)}{g.status === 'closed' ? ' (kapalı)' : ''}
                            </option>
                          ))}
                        <option value="new">+ Yeni grup</option>
                      </select>
                      <select value={p.role || ''}
                        onChange={(e) => setAnnotation(p.positionId, { role: e.target.value })}
                        className={`text-xs rounded-lg border px-2 py-1 ${inputCls} focus:outline-none focus:border-sky-500/60`}>
                        <option value="">rol yok</option>
                        <option value="open">open LP</option>
                        <option value="closing">closing LP</option>
                      </select>

                    </div>

                    {/* Not */}
                    <div className="mb-3">
                      <div className={`text-[11px] font-medium ${muted} mb-1 flex items-center gap-1`}>
                        <StickyNote size={12} /> Not
                      </div>
                      <textarea defaultValue={p.note} placeholder="Bu pozisyon hakkında not ekle…" rows={2}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (p.note || '')) setAnnotation(p.positionId, { note: v }); }}
                        className={`w-full text-xs rounded-lg border px-2.5 py-1.5 resize-y ${inputCls} focus:outline-none focus:border-sky-500/60`} />
                    </div>

                    {/* Hareket defteri */}
                    <div className={`text-[11px] font-medium ${muted} mb-1.5`}>Hareket defteri</div>
                    {!evs && <p className={`text-xs ${muted} py-2`}>Yükleniyor…</p>}
                    {evs && evs.length === 0 && <p className={`text-xs ${muted} py-2`}>Kayıt yok.</p>}
                    {evs && evs.length > 0 && (
                      <div className="space-y-1">
                        {evs.map((e, i) => (
                          <div key={`${e.txHash}-${e.eventSeq}-${i}`}
                            className={`flex items-center gap-3 text-xs px-2 py-1.5 rounded-md ${sub} border ${e.bundled ? 'opacity-50' : ''}`}>
                            <span className={`font-mono ${muted} w-24 shrink-0`}>{fmtDate(e.timestamp)}</span>
                            <span className={`px-2 py-0.5 rounded w-20 text-center shrink-0 text-[10px] ${kindColor(e.kind, dark)}`}>
                              {e.isZap ? (e.kind === 'withdraw' ? 'Zap-out' : 'Zap-in') : (KIND_LABEL[e.kind] || e.kind)}
                            </span>
                            <span className="flex-1 truncate">
                              {e.kind === 'collect_reward'
                                ? `${fmt(e.rewardAmount)} ${e.rewardSymbol}`
                                : (e.kind === 'gas' || e.kind === 'launch_fee')
                                ? `−${fmt(e.rewardAmount, 6)} ${e.rewardSymbol}`
                                : e.isZap
                                  ? (e.kind === 'withdraw'
                                      ? `${fmtSigned(e.amountA)} ${p.symbolA} · ${fmtSigned(e.amountB)} ${p.symbolB}`
                                      : `${fmtSigned(-e.amountA)} ${p.symbolA} · ${fmtSigned(-e.amountB)} ${p.symbolB}`)
                                  : `${fmt(e.amountA)} ${p.symbolA} + ${fmt(e.amountB)} ${p.symbolB}`}
                              {e.bundled && <span className={`ml-1.5 text-[10px] ${muted}`}>(zap-out'a dahil)</span>}
                            </span>
                            <a href={explorerTx(p.chain, e.txHash)} target="_blank" rel="noreferrer"
                              className={`font-mono ${muted} hover:text-sky-400 shrink-0`}>{e.txHash.slice(0, 8)}#{e.eventSeq}</a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* Kademeli render sentinel'i — görününce bir sayfa daha basılır */}
          {tab !== 'groups' && tab !== 'swaps' && visibleCount < filtered.length && (
            <div ref={moreRef} className={`py-4 text-center text-xs ${muted}`}>
              {visibleCount} / {filtered.length} gösteriliyor — kaydırdıkça yükleniyor…
            </div>
          )}
          {/* Yerel liste bittiyse SUNUCUDAN sonraki sayfayı çek (kapalı sekmesi sayfalı) */}
          {tab === 'closed' && hasMore && visibleCount >= filtered.length && (
            <div className="py-4 text-center">
              <button onClick={() => loadPositions(walletRef.current, 'closed', closedOffset)}
                disabled={loading}
                className={`px-4 py-2 rounded-lg border text-xs ${sub} ${muted} hover:text-sky-400 hover:border-sky-500/40 disabled:opacity-50`}>
                {loading ? 'Yükleniyor…' : `Daha fazla yükle (${closedOffset} / ${summary.closed})`}
              </button>
            </div>
          )}
        </div>

        {/* ── Swaplar görünümü ── */}
        {tab === 'swaps' && (
          <>
            {/* Sui'de swap TARANMIYOR — istediğin işlemi digest'iyle sen eklersin.
                Solana tarafı otomatik yakalandığı için orada bu tuş gerekmiyor. */}
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setAddSwap(true)}
                disabled={!isSuiAddr(wallet) && !isEvmAddr(wallet)}
                title={isEvmAddr(wallet) ? 'Arc işlemini tx hash ile ekle'
                       : isSuiAddr(wallet) ? 'Sui işlemini digest ile ekle'
                       : 'Yalnız Sui ya da Arc cüzdanı seçiliyken'}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 disabled:opacity-40">
                + tx ekle
              </button>
              <button onClick={() => setElleSwap(true)} disabled={!wallet}
                title="Borsada yapılan bacak (Bybit, Binance…) — zincirde izi yok"
                className="text-[11px] px-2.5 py-1 rounded-lg border border-violet-500/45 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 disabled:opacity-40">
                + elle ekle
              </button>
              <span className={`text-[11px] ${muted}`}>
                Sui ve Arc swap'ları elle eklenir — Solana'da otomatik yakalanır.
              </span>
            </div>

            {swapSel.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap px-3 py-2 mb-3 rounded-lg border border-sky-500/40 bg-sky-500/10">
                <span className="text-xs font-semibold text-sky-300">{swapSel.size} swap seçildi</span>
                <select value={swapRole} onChange={e => setSwapRole(e.target.value)}
                  className={`text-xs rounded-lg border px-2 py-1 ${inputCls}`}>
                  <option value="">rol yok</option>
                  <option value="open">open</option>
                  <option value="closing">closing</option>
                </select>
                <select defaultValue="" onChange={e => {
                    const v = e.target.value; if (!v) return;
                    // Zincir SABİT YAZILMAMALI: PK (chain, signature) olduğu için
                    // Sui swap'ını 'solana' ile göndermek hiçbir satırı eşleştirmez.
                    // Seçim iki zinciri karıştırabildiğinden zincir bazında gönderiyoruz.
                    const byChain: Record<string, string[]> = {};
                    for (const sg of Array.from(swapSel)) {
                      const c = swaps.find(x => x.signature === sg)?.chain || 'solana';
                      (byChain[c] ||= []).push(sg);
                    }
                    for (const [c, sigs] of Object.entries(byChain)) {
                      sendWs?.({ type: 'set_swap_group', payload: {
                        chain: c, signatures: sigs,
                        groupNo: parseInt(v, 10) || 0, role: swapRole } });
                    }
                    e.target.value = '';
                  }}
                  className={`text-xs rounded-lg border px-2 py-1 ${inputCls}`}>
                  <option value="">Gruba ata…</option>
                  {allGroups.filter((g: any) => (!g.wallet || g.wallet === wallet) && g.status !== 'closed')
                    .sort((a: any, b: any) => a.rank - b.rank)
                    .map((g: any) => <option key={g.no} value={g.no}>{groupLabel(g, g.rank)}</option>)}
                  <option value="0">Bağı kaldır</option>
                </select>
                <button onClick={() => setSwapSel(new Set())}
                  className={`px-3 py-1 rounded-lg text-xs border ${sub} ${muted} hover:text-sky-400`}>Seçimi temizle</button>
              </div>
            )}
            {swaps.length === 0 && (
              <p className={`text-sm ${muted} py-8 text-center`}>
                Bu cüzdanda swap kaydı yok. (Solana geçmişi RPC saklama süresiyle sınırlı.)
              </p>
            )}
            <div className="space-y-1.5">
              {swaps.map(sw => {
                const sel = swapSel.has(sw.signature);
                return (
                  <div key={sw.signature}
                    className={`flex items-center gap-3 text-xs px-3 py-2 rounded-lg border ${card} ${sel ? 'ring-1 ring-sky-500/50' : ''}`}>
                    <input type="checkbox" className="accent-sky-500" checked={sel}
                      onChange={() => setSwapSel(prev => {
                        const n = new Set(prev);
                        n.has(sw.signature) ? n.delete(sw.signature) : n.add(sw.signature);
                        return n;
                      })} />
                    {sw.groupNo > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 border border-sky-500/30 shrink-0">
                        {groupLabel(groupMeta(sw.groupNo), groupMeta(sw.groupNo)?.rank ?? sw.groupNo)}
                        {sw.role ? ` · ${sw.role}` : ''}
                      </span>
                    )}
                    <span className={`font-mono ${muted} w-24 shrink-0`}>{fmtDate(sw.ts)}</span>
                    <span className="tabular-nums text-rose-400">−{fmt(sw.inAmount)} {sw.inSymbol}</span>
                    <span className={muted}>→</span>
                    <span className="tabular-nums text-emerald-400">+{fmt(sw.outAmount)} {sw.outSymbol}</span>
                    <span className={`ml-auto shrink-0 tabular-nums ${muted}`}>
                      {fmtPrice(sw.price)} {sw.outSymbol}/{sw.inSymbol}
                    </span>
                    {sw.chain === MANUAL_CHAIN ? (
                      <span className="shrink-0 flex items-center gap-1.5">
                        {sw.note && <span className={`text-[10px] ${muted} max-w-[9rem] truncate`} title={sw.note}>{sw.note}</span>}
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">elle</span>
                        <button onClick={() => { if (window.confirm('Bu elle eklenen bacak silinsin mi?')) sendWs?.({ type: 'delete_manual_swap', payload: { signature: sw.signature } }); }}
                          title="Sil (yalnız elle eklenen satır silinebilir)"
                          className={`${muted} hover:text-rose-400`}><Trash2 size={12} /></button>
                      </span>
                    ) : (
                      <a href={explorerTx(sw.chain, sw.signature)} target="_blank" rel="noreferrer"
                        className={`font-mono ${muted} hover:text-sky-400 shrink-0`}>{sw.signature.slice(0, 8)}</a>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Gruplar görünümü ── */}
        {tab === 'groups' && groups.length === 0 && (
          <p className={`text-sm ${muted} py-8 text-center`}>
            Henüz grup yok. Bir pozisyonu açıp <span className="text-sky-400">Delta grubu</span> alanına numara girerek gruplayabilirsin.
          </p>
        )}
        {tab === 'groups' && groups.length > 0 && (
          <>
          {/* Yaşayan / Kapalı alt sekmeleri */}
          <div className="flex items-center gap-2 mb-3 text-xs">
            {([['living', 'Yaşayan'], ['closed', 'Kapalı']] as const).map(([k, label]) => {
              const n = groups.filter(g => g.status === k).length;
              return (
                <button key={k} onClick={() => setGroupTab(k)}
                  className={`px-3 py-1.5 rounded-lg border transition ${groupTab === k
                    ? 'bg-sky-500/15 text-sky-400 border-sky-500/40 font-medium'
                    : `${sub} ${muted} hover:text-sky-400`}`}>
                  {label} ({n})
                </button>
              );
            })}
          </div>
          {groups.filter(g => g.status === groupTab).length === 0 && (
            <p className={`text-sm ${muted} py-8 text-center`}>
              {groupTab === 'living' ? 'Yaşayan grup yok.' : 'Kapalı olarak etiketlenmiş grup yok.'}
            </p>
          )}

          {/* Kapalı grupların toplamı — gerçekleşmiş sonuç */}
          {groupTab === 'closed' && closedSummary.count > 0 && (
            <div className={`rounded-xl border ${card} p-3 mb-3 grid grid-cols-2 md:grid-cols-5 gap-3`}>
              <div>
                <div className={`text-[11px] ${muted}`}>Kapalı grup</div>
                <div className="text-lg font-semibold leading-tight">{closedSummary.count}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted} flex items-center gap-1`}><TrendingUp size={11} /> Delta PnL</div>
                <UsdHover tokens={closedSummary.deltaTokens} pop={pop} placement="bottom" align="left">
                  <div className={`text-lg font-semibold leading-tight ${closedSummary.deltaUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {fmtUsd(closedSummary.deltaUsd)}
                  </div>
                </UsdHover>
              </div>
              <div>
                <div className={`text-[11px] ${muted} flex items-center gap-1`}><Receipt size={11} /> Toplam fee</div>
                <UsdHover tokens={closedSummary.feeTokens} pop={pop} placement="bottom" align="left">
                  <div className="text-lg font-semibold leading-tight text-emerald-400">{fmtUsd(closedSummary.feeUsd)}</div>
                </UsdHover>
              </div>
              <div>
                <div className={`text-[11px] ${muted} flex items-center gap-1`}><Gift size={11} /> Toplam reward</div>
                <UsdHover tokens={closedSummary.rewardTokens} pop={pop} placement="bottom" align="left">
                  <div className="text-lg font-semibold leading-tight text-violet-400">{fmtUsd(closedSummary.rewardUsd)}</div>
                </UsdHover>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Toplam</div>
                <UsdHover tokens={closedSummary.totalTokens} pop={pop} placement="bottom" align="right">
                  <div className={`text-lg font-semibold leading-tight ${closedSummary.totalUsd >= 0 ? 'text-sky-400' : 'text-rose-400'}`}>
                    {fmtUsd(closedSummary.totalUsd)}
                  </div>
                </UsdHover>
              </div>
            </div>
          )}
          <div className="space-y-2.5">
            {groups.filter(g => g.status === groupTab).map(renderGroupCard)}
          </div>
          </>
        )}
      </div>

      {/* ── Grup pop-up'ı — Gruplar sekmesindeki kartın AYNISI ── */}
      {groupModal && (() => {
        const g = groups.find(x => x.no === groupModal.no);
        const rank = g?.rank ?? groupModal.rank;
        return (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
            onClick={() => setGroupModal(null)}>
            <div className={`mx-auto w-full max-w-5xl rounded-2xl border ${card} shadow-2xl`}
              onClick={e => e.stopPropagation()}>
              <div className={`flex items-center gap-2 px-4 py-3 border-b ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                <Layers size={15} className="text-sky-400" />
                <span className="text-sm font-semibold">{groupLabel(groupMeta(groupModal.no), rank)}</span>
                <span className={`text-[11px] ${muted}`}>Grup {rank}</span>
                {g?.status === 'closed' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${sub} ${muted} border`}>kapalı</span>
                )}
                <button onClick={() => setGroupModal(null)} title="Kapat (Esc)"
                  className={`ml-auto p-1 rounded-lg ${muted} hover:text-sky-400 transition`}>
                  <X size={16} />
                </button>
              </div>
              <div className="p-3">
                {g ? renderGroupCard(g) : (
                  <p className={`text-sm ${muted} py-10 text-center`}>Grup bilgisi yükleniyor…</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {addSwap && (
        <SuiSwapAddModal dark={dark} wallet={wallet} chain={isEvmAddr(wallet) ? 'arc' : 'sui'} sendWs={sendWs as any}
          subscribe={subscribe as any} onClose={() => setAddSwap(false)}
          onAdded={() => loadPositions(wallet, 'swaps')} />
      )}

      {elleSwap && (
        <ManualSwapModal dark={dark} wallet={wallet} groups={allGroups} groupLabel={groupLabel}
          sendWs={sendWs as any} subscribe={subscribe as any}
          onClose={() => setElleSwap(false)} onSaved={() => loadPositions(wallet, 'swaps')} />
      )}

      {exitPos && (
        <ArcExitModal dark={dark} positionId={exitPos.positionId} wallet={exitPos.wallet}
          label={`${exitPos.symbolA} / ${exitPos.symbolB}`}
          curA={exitPos.curA} curB={exitPos.curB} symbolA={exitPos.symbolA} symbolB={exitPos.symbolB}
          onClose={() => setExitPos(null)}
          onDone={() => forceSync(exitPos.wallet)} />
      )}
    </div>
  );
}

function Metric({ icon, label, a, b, muted }: { icon: React.ReactNode; label: string; a: string; b: string; muted: string }) {
  return (
    <div className="text-right">
      <div className={`flex items-center justify-end gap-1 ${muted} text-[10px]`}>{icon}{label}</div>
      <div className="font-medium">{a}</div>
      {b && <div className={`${muted} text-[10px]`}>{b}</div>}
    </div>
  );
}
function MiniStat({ label, v, sub, muted }: { label: string; v: string; sub: string; muted: string }) {
  return (
    <div className={`rounded-lg border ${sub} p-2`}>
      <div className={`${muted} text-[10px] mb-0.5`}>{label}</div>
      <div className="text-xs font-medium">{v}</div>
    </div>
  );
}
